import Phaser from 'phaser';
import {
  GAME_WIDTH, GAME_HEIGHT, COLORS, HORIZON_Y,
  WALL_X, WALL_Y_START, WALL_Y_END, WALL_WIDTH,
  BASE_SLOT_COLS, BASE_SLOT_ROWS, BASE_SLOT_START_X, BASE_SLOT_END_X,
  BASE_SLOT_START_Y, BASE_SLOT_END_Y, BASE_SLOT_RADIUS,
  BUILDING_POSITIONS, BUILDER_SLOT, FINGER,
  ENEMY_SPAWN_X,
} from '../config';
import { Wall } from '../objects/Wall';
import { Enemy } from '../objects/Enemy';
import { Projectile } from '../objects/Projectile';
import { EnemyArrow } from '../objects/EnemyArrow';
import { BaseUnit } from '../objects/units/BaseUnit';
import { RockThrower } from '../objects/units/RockThrower';
import { SpearThrower } from '../objects/units/SpearThrower';
import { BoomerangThrower } from '../objects/units/BoomerangThrower';
import { Builder } from '../objects/units/Builder';
import { ClubSwinger } from '../objects/units/ClubSwinger';
import { SlingThrower } from '../objects/units/SlingThrower';
import { Archer } from '../objects/units/Archer';
import { LongbowArcher } from '../objects/units/LongbowArcher';

import { CrowTrainer } from '../objects/buildings/CrowTrainer';
import { Trebuchet } from '../objects/buildings/Trebuchet';
import { BeehiveThrower } from '../objects/buildings/BeehiveThrower';
import { Crossbow } from '../objects/buildings/Crossbow';
import { FireflyNest } from '../objects/buildings/FireflyNest';
import { Druid } from '../objects/buildings/Druid';
import { LightningWizard } from '../objects/buildings/LightningWizard';
import { WallCrossbowTurret } from '../objects/buildings/WallCrossbowTurret';
import { AutoClicker } from '../objects/AutoClicker';
import { BeeSwarm } from '../objects/BeeSwarm';
import { WaveManager } from '../managers/WaveManager';
import { EconomyManager } from '../managers/EconomyManager';
import { CombatManager } from '../managers/CombatManager';
import { SkillManager } from '../managers/SkillManager';
import { PrestigeManager } from '../managers/PrestigeManager';
import { achievementManager } from '../managers/AchievementManager';
import { gameStateManager } from '../managers/GameStateManager';
import { BuildingSlot, SlotPosition, EnemyConfig } from '../types';
import { SKILLS, SkillId, ENEMY_MIN_Y, ENEMY_MAX_Y, WALL_X as CFG_WALL_X, UPGRADE_CHAINS, LONGBOW_ARCHER, OFFLINE_GAIN, SCALING, WAVE } from '../config';
import { OfflineGainPopup } from '../ui/OfflineGainPopup';
import { FloatingTextManager } from '../ui/systems/FloatingTextManager';
import { formatNumber } from '../utils/formatNumber';

interface BaseSlot extends SlotPosition {
  unitType: string | null;
  unitRef: BaseUnit | null;
}

export class GameScene extends Phaser.Scene {
  wall!: Wall;
  enemies: Enemy[] = [];
  projectiles: Projectile[] = [];
  enemyArrows: EnemyArrow[] = [];
  beeSwarms: BeeSwarm[] = [];
  units: BaseUnit[] = [];
  buildings: Map<BuildingSlot, CrowTrainer | Trebuchet | BeehiveThrower | Crossbow | FireflyNest | Druid | LightningWizard> = new Map();
  wallTurrets: WallCrossbowTurret[] = [];
  autoClicker: AutoClicker | null = null;
  waveManager!: WaveManager;
  economyManager!: EconomyManager;
  combatManager!: CombatManager;
  skillManager!: SkillManager;
  prestigeManager: PrestigeManager = new PrestigeManager();
  goldEarnedThisRun: number = 0;
  // Gold Rush buff state
  goldRushActive: boolean = false;
  goldRushMultiplier: number = 1;
  goldRushTimer: number = 0;
  goldRushDuration: number = 0;
  isShopPhase: boolean = true;
  isDefeated: boolean = false;
  private bgGraphics!: Phaser.GameObjects.Graphics;
  private baseSlotGraphics!: Phaser.GameObjects.Graphics;
  private defeatText: Phaser.GameObjects.Text | null = null;
  private waveCompleteText: Phaser.GameObjects.Text | null = null;
  private longPressTimer: Phaser.Time.TimerEvent | null = null;

  // Grid of placement slots in the base area
  baseSlots: BaseSlot[] = [];

  // Dedicated builder slot
  builderSlotOccupied: boolean = false;

  // Game speed multiplier (set by UIScene)
  gameSpeedMultiplier: number = 1;
  activeBoss: Enemy | null = null;

  // Boulder independent spawn timer (wave 80+)
  private boulderSpawnTimer: number = 0;

  // Warrior: Blood Frenzy state
  private bloodFrenzyActive: boolean = false;
  private bloodFrenzyTimer: number = 0;
  private recentKillTimes: number[] = [];

  // Warrior: Berserker Instinct state
  private berserkerActive: boolean = false;
  private berserkerTimer: number = 0;
  private berserkerTriggeredThisWave: boolean = false;

  // Mason: Iron Will state
  private ironWillActive: boolean = false;

  // Skill targeting mode
  private targetingSkill: SkillId | null = null;
  private targetingGfx: Phaser.GameObjects.Graphics | null = null;
  private targetingRadius: number = 0;

  // Enemy inspect tooltip
  private tooltipEnemy: Enemy | null = null;
  private tooltipGfx: Phaser.GameObjects.Graphics | null = null;
  private tooltipText: Phaser.GameObjects.Text | null = null;

  // The Finger — chain state delegated to economyManager, transient timer kept here
  get fingerChainMult(): number { return this.economyManager?.fingerChainMult ?? 0; }
  set fingerChainMult(v: number) { if (this.economyManager) this.economyManager.fingerChainMult = v; }
  get fingerDamageFlat(): number { return this.economyManager?.fingerDamageFlat ?? 0; }
  set fingerDamageFlat(v: number) { if (this.economyManager) this.economyManager.fingerDamageFlat = v; }
  get fingerClickGold(): number { return this.economyManager?.fingerClickGold ?? 0; }
  set fingerClickGold(v: number) { if (this.economyManager) this.economyManager.fingerClickGold = v; }
  get fingerAoeRadius(): number { return this.economyManager?.fingerAoeRadius ?? 0; }
  set fingerAoeRadius(v: number) { if (this.economyManager) this.economyManager.fingerAoeRadius = v; }
  fingerAutoClickTimer: number = 0; // Transient — not saved
  get fingerAutoClickInterval(): number { return this.economyManager?.fingerAutoClickInterval ?? 0; }
  set fingerAutoClickInterval(v: number) { if (this.economyManager) this.economyManager.fingerAutoClickInterval = v; }
  private clickImpactGfx!: Phaser.GameObjects.Graphics;

  // sporadic goblin spawner
  goblinSpawnTimer: number = 0;

  // Prestige bonuses computed from elder council
  private _elderBonusCache: import('../types').ElderBonuses | null = null;
  private _elderBonusCacheFrame: number = -1;
  get elderBonuses(): import('../types').ElderBonuses {
    // Cache per frame to avoid recomputing every access
    const frame = this.game?.loop?.frame ?? 0;
    if (!this._elderBonusCache || this._elderBonusCacheFrame !== frame) {
      this._elderBonusCache = this.prestigeManager.getElderBonuses();
      this._elderBonusCacheFrame = frame;
    }
    return this._elderBonusCache;
  }
  get prestigeMult(): number {
    const nb = this.elderBonuses;
    return 1 + nb.damageMult + nb.damageBonusNB;
  }
  get prestigeGoldMult(): number {
    const nb = this.elderBonuses;
    return 1 + nb.goldMult + nb.goldBonusNB;
  }
  get totalKills(): number { return gameStateManager.permanent.totalKills; }
  set totalKills(v: number) { gameStateManager.permanent.totalKills = v; }
  get totalBossKills(): number { return gameStateManager.permanent.totalBossKills; }
  set totalBossKills(v: number) { gameStateManager.permanent.totalBossKills = v; }
  get totalClicks(): number { return gameStateManager.permanent.totalClicks; }
  set totalClicks(v: number) { gameStateManager.permanent.totalClicks = v; }
  get totalGoldEarned(): number { return gameStateManager.permanent.totalGoldEarned; }
  set totalGoldEarned(v: number) { gameStateManager.permanent.totalGoldEarned = v; }

  public floatingTextManager!: FloatingTextManager;

  private isResetting: boolean = false;
  public pendingStartBonuses: boolean = false;

  constructor() {
    super({ key: 'GameScene' });
  }

  create(): void {
    this.enemies = [];
    this.projectiles = [];
    this.units = [];
    this.buildings = new Map();
    this.isShopPhase = true;
    this.isDefeated = false;

    // Draw background
    this.bgGraphics = this.add.graphics();
    this.drawBackground();

    // Create base grid slots
    this.baseSlots = [];
    this.baseSlotGraphics = this.add.graphics();
    this.createBaseSlots();

    // Create wall
    this.wall = new Wall(this);

    // Create managers
    this.waveManager = new WaveManager();
    this.economyManager = new EconomyManager();
    this.combatManager = new CombatManager();
    this.skillManager = new SkillManager();
    this.floatingTextManager = new FloatingTextManager(this);

    // Launch UI scene
    this.scene.launch('UIScene');

    // Initialize achievements
    achievementManager.setScene(this);

    // Listen for achievement unlocks
    achievementManager.on('unlock', (ach: any) => {
      if (ach.bonusType === 'wallHP') {
        this.wall.recalculateMaxHP();
      }
    });

    // Load saved game if available
    gameStateManager.load();

    // Invalidate elder bonus cache so it uses newly loaded permanent state
    this._elderBonusCache = null;

    // Apply any prestige-unlocked slots to the wall
    this.wall.reset();

    let savedRun = gameStateManager.consumeSavedRun();
    const savedTimestamp = gameStateManager.lastSaveTimestamp;
    
    // Safety check: if player bought a prestige upgrade that starts them on a HIGHER
    // wave than their current save, we discard the save and treat it as a fresh start
    if (savedRun && this.elderBonuses.startWave > savedRun.currentWave) {
      savedRun = null;
    }

    if (savedRun) {
      this.restoreFromSave(savedRun);
      // Show offline gain popup if player was away long enough
      this.time.delayedCall(300, () => {
        this.showOfflineGainPopup(savedRun.currentWave, savedTimestamp);
      });
    } else {
      // Fresh start (no save) — apply elder council bonuses (starting wave, units, wall level, etc)
      this.applyStartOfRunBonuses();
    }

    // Always recreate auto-clicker from elder bonuses after permanent state is loaded
    this.refreshAutoClicker();

    // Save on page unload
    window.addEventListener('beforeunload', () => this.saveGame());

    // Click impact graphics
    this.clickImpactGfx = this.add.graphics().setDepth(500);

    // Targeting mode input
    this.targetingGfx = this.add.graphics().setDepth(800);
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.targetingSkill) {
        this.drawTargetingCursor(pointer.x, pointer.y);
      }
    });
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.targetingSkill) {
        if (pointer.rightButtonDown()) {
          this.cancelTargeting();
        } else {
          this.confirmTargeting(pointer.x, pointer.y);
        }
        return;
      }
      // The Finger — click to damage enemies during combat
      if (this.waveManager.isWaveActive && !this.isShopPhase) {
        this.handleFingerClick(pointer.x, pointer.y);
      }

      // Long press for enemy inspect
      this.longPressTimer = this.time.delayedCall(500, () => {
        this.handleEnemyClick(pointer.x, pointer.y);
      });
    });

    this.input.on('pointerup', () => {
      if (this.longPressTimer) {
        this.longPressTimer.remove();
        this.longPressTimer = null;
      }
    });

    this.input.on('pointerout', () => {
      if (this.longPressTimer) {
        this.longPressTimer.remove();
        this.longPressTimer = null;
      }
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.longPressTimer && pointer.getDistance() > 10) {
        this.longPressTimer.remove();
        this.longPressTimer = null;
      }
    });

    this.input.keyboard?.on('keydown-ESC', () => {
      if (this.targetingSkill) {
        this.cancelTargeting();
      }
    });

    // Auto-start wave (small delay to ensure init)
    this.time.delayedCall(100, () => {
      // If we aren't waiting for the prestige tree to close, start the wave
      if (!this.pendingStartBonuses) {
        if (this.waveManager.currentWave > 0) {
          // Restored from save - we are resuming the saved wave, so decrement by 1
          // because startWave() will increment it back.
          this.waveManager.setWave(this.waveManager.currentWave - 1);
        } else {
          // It's wave 0 but for some reason startWave wasn't set > 0, just set to 0
          this.waveManager.setWave(0);
        }
        this.isShopPhase = true;
        this.startWave();
      }
    });
  }


  private createBaseSlots(): void {
    const colSpacing = (BASE_SLOT_END_X - BASE_SLOT_START_X) / Math.max(BASE_SLOT_COLS - 1, 1);
    const rowSpacing = (BASE_SLOT_END_Y - BASE_SLOT_START_Y) / Math.max(BASE_SLOT_ROWS - 1, 1);

    for (let row = 0; row < BASE_SLOT_ROWS; row++) {
      for (let col = 0; col < BASE_SLOT_COLS; col++) {
        this.baseSlots.push({
          x: BASE_SLOT_START_X + col * colSpacing,
          y: BASE_SLOT_START_Y + row * rowSpacing,
          occupied: false,
          unitType: null,
          unitRef: null,
        });
      }
    }
    this.drawBaseSlots();
  }

  drawBaseSlots(): void {
    this.baseSlotGraphics.clear();
    for (const slot of this.baseSlots) {
      if (slot.occupied) {
        // Occupied slot — subtle green ring
        this.baseSlotGraphics.lineStyle(2, COLORS.slotOccupied, 0.5);
        this.baseSlotGraphics.strokeCircle(slot.x, slot.y, BASE_SLOT_RADIUS);
        this.baseSlotGraphics.fillStyle(COLORS.slotOccupied, 0.06);
        this.baseSlotGraphics.fillCircle(slot.x, slot.y, BASE_SLOT_RADIUS);
      } else {
        // Empty slot — light circle
        this.baseSlotGraphics.lineStyle(1.5, COLORS.slotEmpty, 0.25);
        this.baseSlotGraphics.strokeCircle(slot.x, slot.y, BASE_SLOT_RADIUS);
        this.baseSlotGraphics.fillStyle(0xffffff, 0.03);
        this.baseSlotGraphics.fillCircle(slot.x, slot.y, BASE_SLOT_RADIUS);
      }
    }
  }

  private drawBackground(): void {
    // --- STONE AGE SKY with sunset gradient ---
    // Sky gradient from warm orange at horizon to deep blue at top
    for (let y = 0; y < HORIZON_Y; y++) {
      const t = y / HORIZON_Y;
      // Blend from deep blue (top) to warm orange (horizon)
      const r = Math.round(0x40 + (0xe8 - 0x40) * t);
      const g = Math.round(0x60 + (0x90 - 0x60) * t);
      const b = Math.round(0xa0 + (0x70 - 0xa0) * t);
      const skyColor = (r << 16) | (g << 8) | b;
      this.bgGraphics.fillStyle(skyColor, 1);
      this.bgGraphics.fillRect(0, y, GAME_WIDTH, 1);
    }

    // Sun near horizon
    this.bgGraphics.fillStyle(0xffdd44, 0.9);
    this.bgGraphics.fillCircle(850, HORIZON_Y - 20, 28);
    this.bgGraphics.fillStyle(0xffee88, 0.5);
    this.bgGraphics.fillCircle(850, HORIZON_Y - 20, 40);

    // Fluffy clouds
    this.bgGraphics.fillStyle(0xffeecc, 0.6);
    this.drawCloud(150, 35, 0.7);
    this.drawCloud(400, 25, 0.5);
    this.drawCloud(650, 40, 0.6);

    // --- DISTANT MOUNTAINS ---
    this.bgGraphics.fillStyle(0x6b7a8a, 0.7);
    this.bgGraphics.beginPath();
    this.bgGraphics.moveTo(WALL_X + 20, HORIZON_Y + 30);
    this.bgGraphics.lineTo(WALL_X + 100, HORIZON_Y - 15);
    this.bgGraphics.lineTo(WALL_X + 180, HORIZON_Y + 10);
    this.bgGraphics.lineTo(WALL_X + 280, HORIZON_Y - 25);
    this.bgGraphics.lineTo(WALL_X + 380, HORIZON_Y + 5);
    this.bgGraphics.lineTo(WALL_X + 480, HORIZON_Y - 20);
    this.bgGraphics.lineTo(WALL_X + 580, HORIZON_Y + 15);
    this.bgGraphics.lineTo(WALL_X + 700, HORIZON_Y - 10);
    this.bgGraphics.lineTo(GAME_WIDTH, HORIZON_Y + 20);
    this.bgGraphics.lineTo(GAME_WIDTH, HORIZON_Y + 30);
    this.bgGraphics.closePath();
    this.bgGraphics.fillPath();

    // --- Battlefield grass with gradient ---
    const groundTop = HORIZON_Y;
    const groundBottom = GAME_HEIGHT;
    const stripeH = 6;
    for (let y = groundTop; y < groundBottom; y += stripeH) {
      const t = (y - groundTop) / (groundBottom - groundTop);
      // Rich green grass gradient
      const r = Math.round(0x4a + (0x3a - 0x4a) * t);
      const g = Math.round(0x9a + (0x78 - 0x9a) * t);
      const b = Math.round(0x4a + (0x3a - 0x4a) * t);
      const grassColor = (r << 16) | (g << 8) | b;
      this.bgGraphics.fillStyle(grassColor, 1);
      // Grass touches the wall perfectly
      this.bgGraphics.fillRect(WALL_X, y, GAME_WIDTH - WALL_X, stripeH);
    }

    // Grass texture — subtle variations
    for (let y = groundTop + 8; y < groundBottom; y += 16) {
      const alpha = 0.08 + ((y - groundTop) / (groundBottom - groundTop)) * 0.06;
      this.bgGraphics.fillStyle(0x2d702d, alpha);
      this.bgGraphics.fillRect(WALL_X, y, GAME_WIDTH - WALL_X, 2);
    }

    // --- DECORATIVE ELEMENTS ---

    // Scattered rocks
    this.bgGraphics.fillStyle(0x7a7a7a, 0.8);
    this.drawRock(WALL_X + 450, groundTop + 120, 12);
    this.drawRock(WALL_X + 600, groundTop + 280, 8);
    this.drawRock(WALL_X + 350, groundTop + 380, 10);
    this.drawRock(WALL_X + 550, groundTop + 180, 6);

    // Small flowers/plants
    this.drawFlower(WALL_X + 480, groundTop + 100, 0xff6688);
    this.drawFlower(WALL_X + 380, groundTop + 200, 0xffcc44);
    this.drawFlower(WALL_X + 620, groundTop + 340, 0xff6688);
    this.drawFlower(WALL_X + 520, groundTop + 260, 0xffcc44);

    // Grass tufts
    this.bgGraphics.lineStyle(1.5, 0x3d7b3d, 0.4);
    for (let i = 0; i < 40; i++) {
      const gx = WALL_X + 60 + Math.random() * (GAME_WIDTH - WALL_X - 80);
      const gy = groundTop + 30 + Math.random() * (groundBottom - groundTop - 100);
      this.bgGraphics.beginPath();
      this.bgGraphics.moveTo(gx, gy);
      this.bgGraphics.lineTo(gx - 2, gy - 6);
      this.bgGraphics.moveTo(gx + 1, gy);
      this.bgGraphics.lineTo(gx + 2, gy - 5);
      this.bgGraphics.moveTo(gx - 1, gy);
      this.bgGraphics.lineTo(gx, gy - 7);
      this.bgGraphics.strokePath();
    }

    // --- BASE AREA (left of wall) — stone cave floor ---
    // Dark stone gradient
    for (let y = groundTop; y < groundBottom; y += stripeH) {
      const t = (y - groundTop) / (groundBottom - groundTop);
      const shade = Math.round(0x60 + t * 0x20);
      const stoneColor = (shade << 16) | (shade << 8) | shade;
      this.bgGraphics.fillStyle(stoneColor, 1);
      // Fill all the way up to WALL_X so no sky bleeds through
      this.bgGraphics.fillRect(0, y, WALL_X, stripeH);
    }

    // Stone tile grid (rougher, more primitive)
    this.bgGraphics.lineStyle(1.5, 0x555555, 0.2);
    const tileSize = 35;
    for (let ty = groundTop; ty < groundBottom; ty += tileSize) {
      const offset = Math.floor(ty / tileSize) % 2 === 0 ? 0 : tileSize / 2;
      for (let tx = -tileSize / 2 + offset; tx < WALL_X; tx += tileSize) {
        this.bgGraphics.strokeRect(tx, ty, tileSize, tileSize);
      }
    }

    // Cave wall shadow on left
    this.bgGraphics.fillStyle(0x333333, 0.3);
    this.bgGraphics.fillRect(0, groundTop, 20, groundBottom - groundTop);
  }

  private drawRock(x: number, y: number, size: number): void {
    this.bgGraphics.fillStyle(0x6a6a6a, 0.9);
    this.bgGraphics.fillEllipse(x, y, size, size * 0.7);
    this.bgGraphics.fillStyle(0x8a8a8a, 0.4);
    this.bgGraphics.fillEllipse(x - size * 0.2, y - size * 0.2, size * 0.4, size * 0.3);
  }

  private drawFlower(x: number, y: number, color: number): void {
    // Stem
    this.bgGraphics.lineStyle(1, 0x4a7a4a, 0.8);
    this.bgGraphics.beginPath();
    this.bgGraphics.moveTo(x, y);
    this.bgGraphics.lineTo(x, y + 8);
    this.bgGraphics.strokePath();
    // Petals
    this.bgGraphics.fillStyle(color, 0.9);
    for (let i = 0; i < 5; i++) {
      const angle = (i / 5) * Math.PI * 2;
      this.bgGraphics.fillCircle(x + Math.cos(angle) * 3, y + Math.sin(angle) * 3, 2);
    }
    this.bgGraphics.fillStyle(0xffff88, 1);
    this.bgGraphics.fillCircle(x, y, 1.5);
  }

  private drawCloud(x: number, y: number, scale: number): void {
    const s = scale * 18;
    this.bgGraphics.fillCircle(x, y, s);
    this.bgGraphics.fillCircle(x + s * 0.8, y - s * 0.3, s * 0.7);
    this.bgGraphics.fillCircle(x + s * 1.4, y, s * 0.8);
    this.bgGraphics.fillCircle(x + s * 0.5, y + s * 0.2, s * 0.6);
  }

  update(time: number, delta: number): void {
    if (this.isDefeated) return;

    // Track real time played
    gameStateManager.updatePlayTime(delta);

    // Apply game speed multiplier (1x/3x/5x toggle)
    const scaledDelta = delta * this.gameSpeedMultiplier;

    // Sync global upgrade stats
    const achBonuses = achievementManager.getBonuses();
    const elderB = this.elderBonuses;
    const achDmgMult = 1 + achBonuses.damage;

    // Warrior: Blood Frenzy — count down and expire
    if (this.bloodFrenzyActive) {
      this.bloodFrenzyTimer -= scaledDelta;
      if (this.bloodFrenzyTimer <= 0) this.bloodFrenzyActive = false;
    }

    // Warrior: Berserker Instinct — count down and trigger check
    if (this.berserkerActive) {
      this.berserkerTimer -= scaledDelta;
      if (this.berserkerTimer <= 0) this.berserkerActive = false;
    }
    if (elderB.berserkerInstinctEnabled && !this.berserkerTriggeredThisWave && !this.berserkerActive) {
      if (this.wall.currentHP < this.wall.maxHP * 0.25) {
        this.berserkerActive = true;
        this.berserkerTimer = 10000;
        this.berserkerTriggeredThisWave = true;
      }
    }

    // Mason: Iron Will — toggle repair boost based on wall HP thresholds
    if (elderB.ironWillEnabled) {
      const wallRatio = this.wall.currentHP / this.wall.maxHP;
      if (!this.ironWillActive && wallRatio < 0.50) this.ironWillActive = true;
      if (this.ironWillActive && wallRatio > 0.75) this.ironWillActive = false;
    } else {
      this.ironWillActive = false;
    }

    const globalDmgMult = this.prestigeMult * achDmgMult
      * (this.bloodFrenzyActive ? 1.20 : 1.0)
      * (this.berserkerActive ? 2.0 : 1.0);
    const attackSpeedMult = 1 + elderB.attackSpeedMult; // >1 = faster attacks
    // Sync per-frame stats to units and buildings
    const elderRepairMult = (1 + elderB.repairRateMult) * (1 + elderB.repairBonusNB)
      * (this.ironWillActive ? 2.0 : 1.0);
    const wallDefenderDmgMult = globalDmgMult * (1 + elderB.wallSlotDamageMult);
    for (const unit of this.units) {
      unit.globalDamageMult = wallDefenderDmgMult;
      unit.attackSpeedMult = attackSpeedMult;
      // Mason + Builder Efficiency: repair rate bonus for builders
      if (unit instanceof Builder) {
        unit.elderRepairMult = elderRepairMult;
      }
    }
    for (const building of this.buildings.values()) {
      if ('globalDamageMult' in building) {
        (building as any).globalDamageMult = globalDmgMult;
      }
      if ('attackSpeedMult' in building) {
        (building as any).attackSpeedMult = attackSpeedMult;
      }
    }

    // Sync wall turrets
    for (const turret of this.wallTurrets) {
      turret.globalDamageMult = globalDmgMult;
      turret.attackSpeedMult = attackSpeedMult;
      turret.update(0, scaledDelta, this.enemies);
    }

    // Merchant: buy cost discount / chain cost discount
    this.economyManager.elderBuyCostMult = elderB.buyCostMult;
    this.economyManager.elderChainCostMult = elderB.chainCostMult;

    // Auto-clicker (prestige)
    if (this.autoClicker) {
      this.autoClicker.update(scaledDelta, this.enemies);
    }

    // Update skill cooldowns
    this.skillManager.update(scaledDelta);

    // Update gold rush buff timer
    if (this.goldRushActive) {
      this.goldRushTimer -= scaledDelta;
      if (this.goldRushTimer <= 0) {
        this.goldRushActive = false;
        this.goldRushMultiplier = 1;
      }
    }

    // Auto-click (from Finger chain upgrade)
    if (this.fingerAutoClickInterval > 0 && this.waveManager.isWaveActive) {
      this.fingerAutoClickTimer -= scaledDelta;
      if (this.fingerAutoClickTimer <= 0) {
        this.fingerAutoClickTimer = this.fingerAutoClickInterval;
        // Auto-click the enemy with most HP
        let bestEnemy: Enemy | null = null;
        let bestHP = 0;
        for (const enemy of this.enemies) {
          if (!enemy.isDead && enemy.enemyType !== 'boulder' && enemy.hp > bestHP) {
            bestHP = enemy.hp;
            bestEnemy = enemy;
          }
        }
        if (bestEnemy) {
          this.applyFingerDamage(bestEnemy.x, bestEnemy.y);
        }
      }
    }

    if (this.waveManager.isWaveActive) {
      // Spawn enemies
      const spawnResult = this.waveManager.update(scaledDelta);
      if (spawnResult.shouldSpawn && spawnResult.config) {
        const enemy = new Enemy(this, spawnResult.spawnX, spawnResult.spawnY, spawnResult.config);
        if (spawnResult.isBoss) {
          this.activeBoss = enemy;
        }
        this.enemies.push(enemy);
      }

      // Boulder: independent extra spawn from wave 80+ (not part of normal pool)
      if (this.waveManager.currentWave >= 80) {
        this.boulderSpawnTimer -= scaledDelta;
        if (this.boulderSpawnTimer <= 0) {
          // Reset timer: 25–40s between boulders
          this.boulderSpawnTimer = 25000 + Math.random() * 15000;
          const waveConfig = this.waveManager.getWaveConfig(this.waveManager.currentWave);
          const boulderConfig = this.waveManager.getTypedEnemyConfig(waveConfig.enemyConfig, 'boulder');
          const spawnY = ENEMY_MIN_Y + Math.random() * (ENEMY_MAX_Y - ENEMY_MIN_Y);
          const boulder = new Enemy(this, ENEMY_SPAWN_X, spawnY, boulderConfig);
          this.enemies.push(boulder);
          this.waveManager.enemiesRemaining++;
        }
      }

      // Sporadic loot goblin spawner (active on any wave)
      this.trySpawnLootGoblin(scaledDelta);

      // Update enemies (pass all enemies for shaman healing)
      const allEnemies = this.enemies;
      for (const enemy of this.enemies) {
        const wasDead = enemy.isDead;
        enemy.update(time, scaledDelta, this.wall, allEnemies, this.enemyArrows);
        // Handle poison kills (enemy died during update without going through processEnemyKill)
        if (!wasDead && enemy.isDead) {
          this.processEnemyKill(enemy);
        }
      }

      // Update units — handle boss status effects (frozen, stunned, plague)
      const liveEnemies = this.enemies.filter(e => !e.isDead);
      for (const unit of this.units) {
        if (unit.frozenTimer > 0) {
          // Frost Warlord: unit is frozen — skip attack logic, show ice tint and block
          unit.frozenTimer -= scaledDelta;
          unit.sprite.setTint(0x88ccff);
          unit.draw();
          
          // Draw ice block to visually indicate frozen state
          const alpha = Math.min(1, unit.frozenTimer / 500); // fade out nicely at end
          unit.graphics.fillStyle(0x88ccff, alpha * 0.4);
          unit.graphics.fillRect(unit.x - 15, unit.y - 35, 30, 40);
          unit.graphics.lineStyle(2, 0xddffff, alpha * 0.8);
          unit.graphics.strokeRect(unit.x - 15, unit.y - 35, 30, 40);
          
          // Add some inner "shards" or specular highlights
          unit.graphics.lineStyle(1, 0xffffff, alpha * 0.9);
          unit.graphics.beginPath();
          unit.graphics.moveTo(unit.x - 10, unit.y - 25);
          unit.graphics.lineTo(unit.x + 2, unit.y - 15);
          unit.graphics.moveTo(unit.x + 8, unit.y - 20);
          unit.graphics.lineTo(unit.x - 2, unit.y - 10);
          unit.graphics.strokePath();

        } else if (unit.stunnedTimer > 0) {
          // Storm Herald: unit is stunned — skip attack logic, show yellow tint
          unit.stunnedTimer -= scaledDelta;
          unit.sprite.setTint(0xffee88);
          unit.draw();
        } else {
          if (unit.plagueTimer > 0) {
            // Plague Shaman: unit has reduced attack speed
            unit.sprite.setTint(0x66ff66); // Sickly green tint
            unit.plagueTimer -= scaledDelta;
            const saved = unit.attackSpeedMult;
            unit.attackSpeedMult *= 0.4;
            unit.update(time, scaledDelta, liveEnemies);
            unit.attackSpeedMult = saved;
          } else {
            unit.sprite.clearTint();
            unit.update(time, scaledDelta, liveEnemies);
          }
        }

        // Plague Shaman: draw animated poison gas bubbles on plagued units
        // (drawn AFTER unit.draw() runs so they aren't cleared)
        if (unit.plagueTimer > 0) {
          const t = this.time.now;
          for (let i = 0; i < 3; i++) {
            // Use different math to create expanding/popping bubbles
            const cycleSpeed = 0.0015 + (i * 0.0005);
            const cycle = (t * cycleSpeed + i * Math.PI) % (Math.PI * 2);
            const progress = cycle / (Math.PI * 2); // 0 to 1
            
            // Bubbles drift up
            const bx = unit.x + Math.sin(t * 0.003 + i * 2) * 12;
            const by = (unit.y - 10) - (progress * 30);
            
            // Bubbles grow then pop
            const radius = Math.sin(progress * Math.PI) * (4 + i);
            
            if (radius > 0) {
              unit.graphics.fillStyle(0x33ff33, 0.7 * (1 - progress));
              unit.graphics.fillCircle(bx, by, radius);
              unit.graphics.lineStyle(1, 0x11aa11, 0.9 * (1 - progress));
              unit.graphics.strokeCircle(bx, by, radius);
            }
          }
        }
      }

      // Update buildings
      for (const building of this.buildings.values()) {
        building.update(time, scaledDelta, liveEnemies);
      }

      // Update projectiles
      for (const proj of this.projectiles) {
        proj.update(time, scaledDelta);
      }

      // Update enemy arrows
      for (let i = this.enemyArrows.length - 1; i >= 0; i--) {
        const arrow = this.enemyArrows[i];
        if (!arrow.update(time, scaledDelta, this.wall)) {
          this.enemyArrows.splice(i, 1);
        }
      }

      // Update bee swarms
      for (let i = this.beeSwarms.length - 1; i >= 0; i--) {
        const swarm = this.beeSwarms[i];
        if (!swarm.update(scaledDelta)) {
          this.beeSwarms.splice(i, 1);
        } else {
          // Check for enemies in swarm
          for (const enemy of this.enemies) {
            if (enemy.isDead) continue;
            const dist = Math.sqrt((enemy.x - swarm.x) ** 2 + (enemy.y - swarm.y) ** 2);
            if (dist < swarm.radius) {
              // Apply swarm effect
              enemy.applySwarm(100); // Apply small duration constantly while inside
              enemy.applySlow(swarm.slowEffect, 100); // Apply slow effect while inside
            }
          }
        }
      }

      // Check collisions
      const { killed } = this.combatManager.checkProjectileEnemyCollisions(
        this.projectiles.filter(p => !p.isDestroyed),
        liveEnemies
      );

      // Process kills and apply slow effects
      let totalKills = 0;
      let bossKills = 0;

      for (const enemy of killed) {
        this.processEnemyKill(enemy);
        totalKills++; // Keep local tracking for batch achievements if needed? 
        // Actually processEnemyKill handles gold/wave progress.
        // But achievement batching is done below. 
        // Let's defer achievement updates or just do them one by one?
        // The original code did batch addGold for achievements.
        // Let's keep it simple: processEnemyKill handles single kill rewards.
        // Achievement tracking relies on totalKills count in this frame... 
        // Logic below uses local totalKills/bossKills variables.
        // We should explicitly count them here to match original logic.
      }

      // We can also have ClubSwinger usages call processEnemyKill directly.
      // But we need to ensure they also trigger achievement checks if we depend on this loop.
      // Wait, if ClubSwinger calls `processEnemyKill` immediately during update, 
      // it won't be counted in `totalKills` here.
      // So detailed achievement tracking (kills in one frame?) isn't super critical 
      // but "total kills" is.

      // Let's look at processEnemyKill implementation below.


      // Track wall damage and repair from Wall's built-in counters
      achievementManager.checkProgress('wall_damage', this.wall.totalDamageTaken);
      achievementManager.checkProgress('repair', this.wall.totalRepaired);

      // Check various achievements
      achievementManager.checkProgress('gold_held', this.economyManager.gold);
      achievementManager.checkProgress('gold_earned', this.totalGoldEarned);
      achievementManager.checkProgress('units_owned', this.economyManager.getTotalUnitCount());

      // Per-unit-type ownership achievements
      const unitTypes = [
        'rock_thrower', 'archer', 'club_swinger', 'sling_thrower', 'spear_thrower', 'builder',
        'longbow_archer', 'crow_trainer', 'beehive_thrower', 'trebuchet',
        'crossbow', 'firefly_nest', 'druid', 'lightning_wizard', 'boomerang_thrower',
      ];
      for (const ut of unitTypes) {
        achievementManager.checkProgress(`${ut}_owned`, this.economyManager.getUnitCount(ut));
      }

      // Slow effects are now applied in CombatManager when projectiles hit enemies

      // Clean up destroyed projectiles in-place (units hold a reference to this array)
      for (let i = this.projectiles.length - 1; i >= 0; i--) {
        if (this.projectiles[i].isDestroyed) this.projectiles.splice(i, 1);
      }
      // Clean up dead enemies
      for (let i = this.enemies.length - 1; i >= 0; i--) {
        if (this.enemies[i].isDead) {
          // Ensure kill processing runs if it hasn't already (e.g. from poison/dot)
          if (!this.enemies[i].isProcessed) {
            this.processEnemyKill(this.enemies[i]);
          }

          if (this.enemies[i] === this.activeBoss) {
            this.activeBoss = null;
          }
          this.enemies.splice(i, 1);
        }
      }

      // Update enemy inspect tooltip (follow enemy, update stats)
      if (this.tooltipEnemy) {
        this.updateEnemyTooltip();
      }

      // Check wall destruction
      if (this.wall.isDestroyed()) {
        this.handleDefeat();
        return;
      }
    }

    // Check wave complete
    if (this.waveManager.currentWave > 0 && this.waveManager.isWaveComplete()) {
      this.handleWaveComplete();
    }
  }

  public processEnemyKill(enemy: Enemy): void {
    if (enemy.isProcessed) return;
    enemy.isProcessed = true;

    try {
      // Critical: Update WaveManager state first to prevent progression blocks
      // Loot Goblin is excluded from wave tracking so it doesn't block wave progression
      if (enemy.enemyType !== 'loot_goblin') {
        this.waveManager.onEnemyKilled(enemy.isBoss);
      }

      const rushMult = this.goldRushActive ? this.goldRushMultiplier : 1;
      const achGoldBonus = achievementManager.getBonuses().gold;
      const bossGoldMult = this.elderBonuses.bossGoldMultOverride > 0 ? this.elderBonuses.bossGoldMultOverride : 15;
      const isBossKill = enemy === this.activeBoss;
      let reward = enemy.goldReward * rushMult * this.prestigeGoldMult * (1 + achGoldBonus)
        * (isBossKill && enemy.goldReward > 0 ? bossGoldMult / 15 : 1);

      // Merchant: Windfall — 15% chance to grant 50% bonus gold on this kill
      if (this.elderBonuses.windfallChance > 0 && enemy.goldReward > 0 && Math.random() < this.elderBonuses.windfallChance) {
        reward *= 1.5;
      }

      this.economyManager.addGold(reward);
      this.totalGoldEarned += reward;
      this.goldEarnedThisRun += reward;

      // Warrior: Blood Frenzy — track kill times, trigger if 3 in 1s
      if (this.elderBonuses.bloodFrenzyEnabled) {
        const now = this.time.now;
        this.recentKillTimes.push(now);
        // Prune kills older than 1s
        const cutoff = now - 1000;
        while (this.recentKillTimes.length > 0 && this.recentKillTimes[0] < cutoff) {
          this.recentKillTimes.shift();
        }
        if (this.recentKillTimes.length >= 3) {
          this.bloodFrenzyActive = true;
          this.bloodFrenzyTimer = 3000;
          this.recentKillTimes.length = 0; // Reset to avoid re-triggering immediately
        }
      }

      // Safety check for FloatingTextManager
      if (this.floatingTextManager) {
        this.floatingTextManager.spawn(enemy.x, enemy.y, `+${formatNumber(Math.floor(reward))}g`, 'gold');
      } else {
        console.warn('[GameScene] floatingTextManager is missing!');
      }

      // Track kills
      this.totalKills++;
      achievementManager.checkProgress('kills', this.totalKills);

      if (enemy === this.activeBoss) {
        this.totalBossKills++;
        achievementManager.checkProgress('boss_kills', this.totalBossKills);
      }

      // Per-type kill tracking
      if (enemy.enemyType === 'brute' || enemy.isSplitChild) {
        gameStateManager.permanent.bruteKills++;
        achievementManager.checkProgress('brute_kills', gameStateManager.permanent.bruteKills);
      } else if (enemy.enemyType === 'shaman') {
        gameStateManager.permanent.shamanKills++;
        achievementManager.checkProgress('shaman_kills', gameStateManager.permanent.shamanKills);
      } else if (enemy.enemyType === 'boulder') {
        gameStateManager.permanent.boulderKills++;
        achievementManager.checkProgress('boulder_kills', gameStateManager.permanent.boulderKills);
      }

      // Splitter: spawn 2 mini-stickmen on death (unless this is already a split child)
      if (enemy.enemyType === 'splitter' && !enemy.isSplitChild) {
        const availableTypes = this.waveManager.getEnemyTypesForWave(this.waveManager.currentWave)
          .filter(t => t !== 'splitter' && t !== 'nest_carrier' && t !== 'enemy_trebuchet' && t !== 'swarmling');
        const waveConfig = this.waveManager.getWaveConfig(this.waveManager.currentWave);

        for (let i = 0; i < 2; i++) {
          // Pick a random type for each child
          const childType = availableTypes[Math.floor(Math.random() * availableTypes.length)] || 'basic';
          const typedConfig = this.waveManager.getTypedEnemyConfig(waveConfig.enemyConfig, childType);

          // Scale down for split child
          typedConfig.hp = Math.round(typedConfig.hp * 0.3);
          typedConfig.damage = Math.round(typedConfig.damage * 0.5);
          typedConfig.goldReward = Math.round(typedConfig.goldReward * 0.25);
          typedConfig.isSplitChild = true;
          typedConfig.scale = 0.7;

          const offsetY = (i === 0 ? -15 : 15);
          const child = new Enemy(this, enemy.x + 10, enemy.y + offsetY, typedConfig);
          this.enemies.push(child);
          this.waveManager.enemiesRemaining++;
        }
      }

      // Loot Goblin: gold fountain on death — return 5x what it stole
      if (enemy.enemyType === 'loot_goblin' && enemy.goblinStolenGold > 0) {
        const goblinReward = enemy.goblinStolenGold * 5;
        this.economyManager.addGold(goblinReward);
        this.totalGoldEarned += goblinReward;
        this.goldEarnedThisRun += goblinReward;
        this.floatingTextManager?.spawn(enemy.x, enemy.y - 30, `+${formatNumber(Math.floor(goblinReward))}g`, 'gold');

        // Spawn gold coin particle fountain
        for (let i = 0; i < 12; i++) {
          const coin = this.add.graphics().setDepth(500);
          coin.setPosition(enemy.x, enemy.y);
          coin.fillStyle(0xffd700, 1);
          coin.fillCircle(0, 0, 3);
          coin.fillStyle(0xffaa00, 1);
          coin.fillCircle(1, -1, 1.5);
          const angle = (Math.PI * 2 / 12) * i + (Math.random() - 0.5) * 0.5;
          const dist = 40 + Math.random() * 60;
          const targetX = enemy.x + Math.cos(angle) * dist;
          const targetY = enemy.y - 20 + Math.sin(angle) * dist * 0.6;
          this.tweens.add({
            targets: coin,
            x: targetX,
            y: targetY,
            alpha: 0,
            duration: 600 + Math.random() * 400,
            ease: 'Cubic.easeOut',
            onComplete: () => coin.destroy()
          });
        }
      }

      console.log('[GameScene] Kill processing finished successfully.');
    } catch (e) {
      console.error('[GameScene] Error in processEnemyKill:', e);
    }
  }

  /** Spawn a basic enemy child from a siege tower at the wall */
  public spawnSiegeTowerChild(parent: Enemy): void {
    const waveConfig = this.waveManager.getWaveConfig(this.waveManager.currentWave);
    const childConfig = this.waveManager.getTypedEnemyConfig(waveConfig.enemyConfig, 'basic');
    childConfig.hp = Math.round(childConfig.hp * 0.5);
    childConfig.damage = Math.round(childConfig.damage * 0.6);
    childConfig.goldReward = Math.round(childConfig.goldReward * 0.3);
    // Spawn near the wall
    const wallX = WALL_X + WALL_WIDTH + 25;
    const spawnY = parent.y + (Math.random() - 0.5) * 40;
    const child = new Enemy(this, wallX, spawnY, childConfig);
    this.enemies.push(child);
    this.waveManager.enemiesRemaining++;
  }

  /** Sporadic loot goblin spawner — checked in update loop */
  private trySpawnLootGoblin(delta: number): void {
    if (!this.waveManager.isWaveActive) return;
    this.goblinSpawnTimer -= delta;
    if (this.goblinSpawnTimer <= 0) {
      // Reset timer: 30-60s between goblins
      this.goblinSpawnTimer = 30000 + Math.random() * 30000;
      
      // Only 1 loot goblin allowed at a time
      const hasGoblin = this.enemies.some(e => !e.isDead && e.enemyType === 'loot_goblin');
      
      // ~40% chance to actually spawn one
      if (Math.random() < 0.4 && !hasGoblin) {
        const waveConfig = this.waveManager.getWaveConfig(this.waveManager.currentWave);
        const goblinConfig = this.waveManager.getTypedEnemyConfig(waveConfig.enemyConfig, 'loot_goblin');
        const spawnY = ENEMY_MIN_Y + Math.random() * (ENEMY_MAX_Y - ENEMY_MIN_Y);
        const goblin = new Enemy(this, ENEMY_SPAWN_X, spawnY, goblinConfig);
        this.enemies.push(goblin);
        // Excluded from wave tracking: this.waveManager.enemiesRemaining++;
        this.floatingTextManager?.spawn(ENEMY_SPAWN_X - 40, spawnY, '💰 Loot Goblin!', 'gold');
      }
    }
  }

  /** Spawn a swarmling child for a nest_carrier enemy */
  /** Recreate / update the AutoClicker based on current elder bonuses. Safe to call at any time. */
  public refreshAutoClicker(): void {
    const interval = this.elderBonuses.autoClickInterval;
    if (interval > 0) {
      if (!this.autoClicker) {
        this.autoClicker = new AutoClicker(this, (x, y) => this.handleFingerClick(x, y));
      }
      this.autoClicker.setInterval(interval);
    } else {
      if (this.autoClicker) {
        this.autoClicker.destroy();
        this.autoClicker = null;
      }
    }
  }

  public spawnBoulderChild(boulder: Enemy): void {
    if (boulder.isDead) return;

    const waveConfig = this.waveManager.getWaveConfig(this.waveManager.currentWave);
    const childConfig = this.waveManager.getTypedEnemyConfig(waveConfig.enemyConfig, 'brute');
    childConfig.hp = Math.round(childConfig.hp * 0.35);
    childConfig.damage = Math.round(childConfig.damage * 0.6);
    childConfig.goldReward = Math.round(childConfig.goldReward * 0.4);
    childConfig.speed = 35;
    (childConfig as any).scale = 0.85;
    childConfig.isSplitChild = true; // Marks as a spawned child

    const offsetY = (Math.random() - 0.5) * 50;
    const child = new Enemy(this, boulder.x + 8, boulder.y + offsetY, childConfig);
    child.isAttacking = true; // Already at wall — start attacking immediately
    this.enemies.push(child);
    this.waveManager.enemiesRemaining++;
  }

  public spawnNestChild(parent: Enemy): void {
    if (parent.isDead || parent.nestChildCount >= Enemy.NEST_MAX_CHILDREN) return;

    const waveConfig = this.waveManager.getWaveConfig(this.waveManager.currentWave);
    const baseConfig = waveConfig.enemyConfig;
    const childConfig = this.waveManager.getTypedEnemyConfig(baseConfig, 'swarmling');
    childConfig.goldReward = 0; // Nestlings give no gold

    const offsetY = (Math.random() - 0.5) * 30;
    const child = new Enemy(this, parent.x + 5, parent.y + offsetY, childConfig);
    this.enemies.push(child);
    this.waveManager.enemiesRemaining++;
    parent.nestChildCount++;
  }

  /** Called by a boss Enemy when its ability fires */
  public onBossAbility(ability: string, boss: Enemy): void {
    const liveUnits = this.units;

    if (ability === 'frost') {
      // Freeze 2 random units for 3 seconds
      const shuffled = [...liveUnits].sort(() => Math.random() - 0.5);
      const targets = shuffled.slice(0, 2);
      for (const unit of targets) {
        unit.frozenTimer = 3000;
      }
      // Visual: expanding ice ring at boss position
      const ring = this.add.graphics();
      ring.lineStyle(3, 0x00ddff, 0.9);
      ring.strokeCircle(boss.x, boss.y - 20, 5);
      this.tweens.add({
        targets: ring,
        duration: 600,
        onUpdate: (_tween: Phaser.Tweens.Tween, _target: any, _key: string, current: number) => {
          ring.clear();
          const prog = _tween.progress;
          ring.lineStyle(3, 0x00ddff, (1 - prog) * 0.9);
          ring.strokeCircle(boss.x, boss.y - 20, 5 + prog * 90);
        },
        onComplete: () => ring.destroy(),
      });

    } else if (ability === 'lava') {
      // Burn wall for 1% max HP
      const burnDmg = Math.max(1, Math.floor(this.wall.maxHP * 0.01));
      this.wall.takeDamage(burnDmg);
      
      const wx = WALL_X + WALL_WIDTH / 2;
      const wy = boss.y + (Math.random() - 0.5) * 60;
      
      // Floating text
      this.floatingTextManager?.spawn(wx + 20, wy, 'BURN!', 'damage');

      // Visual: orange blob splat at wall
      const splat = this.add.graphics();
      splat.fillStyle(0xff6600, 0.8);
      splat.fillCircle(wx, wy, 10 + Math.random() * 8);
      splat.fillStyle(0xff9900, 0.5);
      splat.fillCircle(wx + (Math.random() - 0.5) * 10, wy - 5, 6);
      this.tweens.add({
        targets: splat,
        alpha: 0,
        duration: 1500,
        onComplete: () => splat.destroy(),
      });

      // Wall flash: draw a red block over the impact area
      const wallFlash = this.add.graphics();
      wallFlash.fillStyle(0xff0000, 0.6);
      wallFlash.fillRect(WALL_X, wy - 40, WALL_WIDTH, 80);
      this.tweens.add({
        targets: wallFlash,
        alpha: 0,
        duration: 400,
        onComplete: () => wallFlash.destroy(),
      });

    } else if (ability === 'storm') {
      // Stun all units for 2.5 seconds
      for (const unit of liveUnits) {
        unit.stunnedTimer = 2500;
      }
      // Visual: yellow flash across all units
      for (const unit of liveUnits) {
        const bolt = this.add.graphics();
        bolt.lineStyle(2, 0xffee00, 1);
        bolt.beginPath();
        bolt.moveTo(boss.x, boss.y - 20);
        const mx = (boss.x + unit.x) / 2 + (Math.random() - 0.5) * 40;
        const my = Math.min(boss.y, unit.y) - 40 - Math.random() * 30;
        bolt.lineTo(mx, my);
        bolt.lineTo(unit.x, unit.y - 20);
        bolt.strokePath();
        this.tweens.add({ targets: bolt, alpha: 0, duration: 400, onComplete: () => bolt.destroy() });
      }

    } else if (ability === 'plague') {
      // Apply plague to all units: reduced attack speed for 4 seconds
      for (const unit of liveUnits) {
        unit.plagueTimer = 4000;
      }
      // Visual: green cloud burst at boss
      const cloud = this.add.graphics();
      cloud.fillStyle(0x44ff44, 0.35);
      cloud.fillCircle(boss.x, boss.y - 20, 20);
      this.tweens.add({
        targets: cloud,
        scaleX: 5, scaleY: 3,
        alpha: 0,
        duration: 800,
        ease: 'Sine.easeOut',
        onComplete: () => cloud.destroy(),
      });
    }
  }

  /** Spawn a fading shadow ghost at the given position (Shadow Stalker teleport) */
  public spawnShadowGhost(x: number, y: number): void {
    const ghost = this.add.graphics();
    ghost.fillStyle(0x440066, 0.6);
    ghost.fillCircle(0, 0, 18);
    ghost.fillStyle(0x6600aa, 0.4);
    ghost.fillCircle(0, -10, 12);
    ghost.setPosition(x, y);
    this.tweens.add({
      targets: ghost,
      alpha: 0,
      scaleX: 1.5,
      scaleY: 1.5,
      duration: 700,
      ease: 'Sine.easeOut',
      onComplete: () => ghost.destroy(),
    });
  }

  startWave(): void {
    if (this.isShopPhase && !this.isDefeated) {
      this.isShopPhase = false;
      // this.clearWaveCompleteText(); // Let handleWaveComplete clear it after delay
      this.waveManager.startWave();
      // Reset boulder timer so first boulder arrives 15-25s into the wave
      this.boulderSpawnTimer = 15000 + Math.random() * 10000;
      // Reset per-wave Berserker trigger
      this.berserkerTriggeredThisWave = false;
      this.events.emit('wave-started', this.waveManager.currentWave);
    }
  }

  private handleWaveComplete(): void {
    // Check wave achievements
    achievementManager.checkProgress('wave', this.waveManager.currentWave);

    // Auto-save after each wave
    this.saveGame();

    // Clear any previous wave text to prevent lingering
    this.clearWaveCompleteText();

    this.waveCompleteText = this.add.text(
      (WALL_X + GAME_WIDTH) / 2, GAME_HEIGHT / 2 - 60,
      `Wave ${this.waveManager.currentWave} Complete!`,
      {
        fontSize: '32px',
        fontFamily: 'Arial Black, Arial, sans-serif',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 4,
      }
    ).setOrigin(0.5);

    this.time.delayedCall(2000, () => { this.clearWaveCompleteText(); });
    this.events.emit('wave-complete', this.waveManager.currentWave);

    // Seamless transition: Immediately start next wave
    if (!this.isDefeated) {
      this.isShopPhase = false;
      this.waveManager.startWave();
      this.events.emit('wave-started', this.waveManager.currentWave);
    }
  }

  private clearWaveCompleteText(): void {
    if (this.waveCompleteText) {
      this.waveCompleteText.destroy();
      this.waveCompleteText = null;
    }
  }

  private handleDefeat(): void {
    this.isDefeated = true;
    const restartWave = this.waveManager.getRestartWave();

    const preservedEnemies: Enemy[] = [];
    for (const enemy of this.enemies) {
      if (enemy.enemyType === 'loot_goblin') {
        preservedEnemies.push(enemy);
      } else {
        enemy.destroy();
      }
    }
    this.enemies = preservedEnemies;
    this.activeBoss = null;
    for (const proj of this.projectiles) { proj.destroy(); }
    this.projectiles.length = 0;
    for (const arrow of this.enemyArrows) { arrow.destroy(); }
    this.enemyArrows.length = 0;

    this.defeatText = this.add.text(
      (WALL_X + GAME_WIDTH) / 2, GAME_HEIGHT / 2 - 60,
      `Wall Destroyed!\nReturning to Wave ${restartWave + 1}...`,
      {
        fontSize: '28px',
        fontFamily: 'Arial Black, Arial, sans-serif',
        color: '#ff4444',
        stroke: '#000000',
        strokeThickness: 4,
        align: 'center',
      }
    ).setOrigin(0.5);

    this.time.delayedCall(3000, () => {
      if (this.defeatText) { this.defeatText.destroy(); this.defeatText = null; }
      this.waveManager.setWave(restartWave);
      this.wall.resetHP();
      this.isDefeated = false;
      this.isShopPhase = true;
      this.events.emit('defeat-reset', restartWave);

      // Auto-start wave after defeat reset
      this.time.delayedCall(2000, () => {
        if (this.isShopPhase && !this.isDefeated) {
          this.startWave();
        }
      });
    });
  }

  private getNextFreeBaseSlot(): BaseSlot | null {
    return this.baseSlots.find(s => !s.occupied) || null;
  }

  // Called from ShopPanel with "buy_" or "upgrade_" prefix
  buyUnit(key: string, quantity: number = 1): boolean {
    if (key === 'reset_game') {
      this.isResetting = true;
      // Overwrite the saveGame function entirely so the beforeunload event CANNOT trigger a save
      this.saveGame = () => { };

      // Clear all stored data
      localStorage.removeItem('stickdefence_save');
      gameStateManager.clearSave();

      // Reload page to start fresh
      window.location.reload();
      return true;
    }

    // Route buy vs upgrade actions
    if (key.startsWith('buy_')) {
      const unitType = key.replace('buy_', '');
      // Check if this is a skill purchase
      if (unitType in SKILLS) {
        return this.handleBuySkill(unitType as SkillId);
      }
      // Bulk buy: -1 = max affordable, otherwise buy N times
      const count = quantity === -1
        ? this.economyManager.getMaxAffordable(unitType)
        : quantity;
      let bought = false;
      for (let i = 0; i < count; i++) {
        if (!this.handleBuy(unitType)) break;
        bought = true;
      }
      return bought;
    } else if (key.startsWith('upgrade_')) {
      const unitType = key.replace('upgrade_', '');
      // Check if this is a skill upgrade
      if (unitType in SKILLS) {
        return this.handleUpgradeSkill(unitType as SkillId);
      }
      return this.handleUpgrade(unitType);
    }
    return false;
  }

  private handleBuySkill(skillId: SkillId): boolean {
    if (this.skillManager.isOwned(skillId)) return false;
    const cost = this.economyManager.getBuyCost(skillId);
    if (!this.economyManager.spendGold(cost)) return false;
    this.economyManager.unitCounts[skillId] = 1;
    this.skillManager.buySkill(skillId);
    return true;
  }

  private handleUpgradeSkill(skillId: SkillId): boolean {
    if (!this.skillManager.isOwned(skillId)) return false;
    if (!this.economyManager.purchaseUpgrade(skillId)) return false;
    this.skillManager.upgradeSkill(skillId);
    return true;
  }

  // Place a new unit instance OR add to existing stack
  // Place a new unit instance OR add to existing stack
  private handleBuy(unitType: string, free: boolean = false): boolean {
    // ---- WALL CROSSBOW TURRETS ----
    if (unitType === 'wall_crossbow_top' || unitType === 'wall_crossbow_bottom') {
      const position = unitType === 'wall_crossbow_top' ? 'top' : 'bottom';
      
      const existingTurret = this.wallTurrets.find(t => t.position === position);
      if (existingTurret) {
        if (free) this.economyManager.addFreeUnit(unitType);
        else if (!this.economyManager.purchaseBuy(unitType)) return false;
        existingTurret.addStack();
        return true;
      }
      
      // Creating the very first instance
      if (free) this.economyManager.addFreeUnit(unitType);
      else if (!this.economyManager.purchaseBuy(unitType)) return false;
      
      const turret = new WallCrossbowTurret(this, position as 'top' | 'bottom', this.projectiles);
      this.applyChainProgressToTurret(turret, unitType);
      this.wallTurrets.push(turret);
      return true;
    }

    // ---- LONGBOW ARCHER SPECIAL LOGIC ----
    // Longbow only occupies front slot 1 (baseSlots index 1)
    if (unitType === 'longbow_archer') {
      const existingLongbows = this.units.filter(u => u instanceof LongbowArcher);
      const longbowSlot = this.baseSlots[1]; // Front row 0, dedicated longbow slot

      if (!longbowSlot.occupied) {
        if (free) this.economyManager.addFreeUnit(unitType);
        else if (!this.economyManager.purchaseBuy(unitType)) return false;
        longbowSlot.occupied = true;
        longbowSlot.unitType = unitType;
        const unit = new LongbowArcher(this, longbowSlot.x, longbowSlot.y, 1, this.projectiles);
        longbowSlot.unitRef = unit;
        this.applyChainProgress(unit, unitType);
        this.units.push(unit);
        return true;
      } else {
        // Slot occupied → stack on existing longbow
        if (existingLongbows.length > 0) {
          const target = existingLongbows[Math.floor(Math.random() * existingLongbows.length)];
          if (free) this.economyManager.addFreeUnit(unitType);
          else if (!this.economyManager.purchaseBuy(unitType)) return false;
          target.addStack();
          return true;
        }
        return false;
      }
    }

    // ---- STANDARD UNITS (Stacking Logic) ----
    const existingUnit = this.findExistingUnit(unitType);

    if (existingUnit) {
      if (free) this.economyManager.addFreeUnit(unitType);
      else if (!this.economyManager.purchaseBuy(unitType)) return false;
      existingUnit.addStack();
      return true;
    }

    // ---- CREATE FIRST INSTANCE ----
    let unit;
    let slot;

    // Wall Units
    if (['rock_thrower', 'archer', 'club_swinger', 'sling_thrower', 'spear_thrower', 'boomerang_thrower'].includes(unitType)) {
      slot = this.wall.getNextFreeSlot();
      if (!slot) return false;
      if (free) this.economyManager.addFreeUnit(unitType);
      else if (!this.economyManager.purchaseBuy(unitType)) return false;
      slot.occupied = true;

      const slotIndex = this.wall.slots.indexOf(slot);

      if (unitType === 'rock_thrower') unit = new RockThrower(this, slot.x, slot.y, slotIndex, this.projectiles);
      else if (unitType === 'archer') unit = new Archer(this, slot.x, slot.y, slotIndex, this.projectiles);
      else if (unitType === 'club_swinger') unit = new ClubSwinger(this, slot.x, slot.y, slotIndex);
      else if (unitType === 'sling_thrower') unit = new SlingThrower(this, slot.x, slot.y, slotIndex, this.projectiles);
      else if (unitType === 'spear_thrower') unit = new SpearThrower(this, slot.x, slot.y, slotIndex, this.projectiles);
      else if (unitType === 'boomerang_thrower') unit = new BoomerangThrower(this, slot.x, slot.y, slotIndex, this.projectiles);

      this.wall.draw();
    }
    // Builder
    else if (unitType === 'builder') {
      if (free) this.economyManager.addFreeUnit(unitType);
      else if (!this.economyManager.purchaseBuy(unitType)) return false;
      unit = new Builder(this, BUILDER_SLOT.x, BUILDER_SLOT.y, -1, this.wall);
    }
    // Base Units - Restricted Placement (back column)
    else if (unitType === 'crow_trainer') return this.buyBaseUnit(unitType, 'below_1', CrowTrainer, free);
    else if (unitType === 'druid') return this.buyBaseUnit(unitType, 'below_2', Druid, free);
    else if (unitType === 'trebuchet') return this.buyBaseUnit(unitType, 'behind_1', Trebuchet, free);
    else if (unitType === 'lightning_wizard') return this.buyBaseUnit(unitType, 'behind_2', LightningWizard, free);
    // Front column buildings
    else if (unitType === 'crossbow') return this.buyBaseUnit(unitType, 'front_3', Crossbow, free);
    else if (unitType === 'firefly_nest') return this.buyBaseUnit(unitType, 'front_2', FireflyNest, free);
    else if (unitType === 'beehive_thrower') return this.buyBaseUnit(unitType, 'front_4', BeehiveThrower, free);

    if (unit) {
      this.applyChainProgress(unit, unitType);
      this.units.push(unit);
      return true;
    }

    return false;
  }

  private buyBaseUnit(unitType: string, slotKey: BuildingSlot, UnitClass: any, free: boolean = false): boolean {
    const existingUnit = this.buildings.get(slotKey);
    if (existingUnit) {
      // Add to existing stack
      if (free) this.economyManager.addFreeUnit(unitType);
      else if (!this.economyManager.purchaseBuy(unitType)) return false;
      existingUnit.addStack();
      return true;
    }

    // Map slot keys to baseSlots indices
    // Back column (col 0): indices 0, 2, 4, 6 (rows 0-3)
    // Front column (col 1): indices 1, 3, 5, 7 (rows 0-3)
    const slotMap: Record<string, number> = {
      'below_1': 0,   // Back row 0 (top-most)
      'below_2': 2,   // Back row 1
      'behind_1': 4,  // Back row 2
      'behind_2': 6,  // Back row 3 (bottom-most)
      'front_2': 3,   // Front row 1
      'front_3': 5,   // Front row 2
      'front_4': 7,   // Front row 3
    };

    const slotIndex = slotMap[slotKey];
    if (slotIndex === undefined) return false;
    const targetSlot = this.baseSlots[slotIndex];

    if (!targetSlot || targetSlot.occupied) {
      return false; // Slot taken by something else (shouldn't happen if restricted)
    }

    if (free) this.economyManager.addFreeUnit(unitType);
    else if (!this.economyManager.purchaseBuy(unitType)) return false;

    targetSlot.occupied = true;
    targetSlot.unitType = unitType;

    // Instantiate
    const unit = new UnitClass(this, targetSlot.x, targetSlot.y, slotKey, this.projectiles);

    // Apply chain progress to new building
    this.applyBuildingChainProgress(unit, unitType);

    targetSlot.unitRef = unit;
    this.buildings.set(slotKey, unit);
    return true;
  }


  // Find an existing unit of the given type
  private findExistingUnit(unitType: string): BaseUnit | null {
    for (const unit of this.units) {
      if (unitType === 'rock_thrower' && unit instanceof RockThrower) return unit;
      if (unitType === 'spear_thrower' && unit instanceof SpearThrower) return unit;
      if (unitType === 'club_swinger' && unit instanceof ClubSwinger) return unit;
      if (unitType === 'archer' && unit instanceof Archer) return unit;
      if (unitType === 'sling_thrower' && unit instanceof SlingThrower) return unit;
      if (unitType === 'builder' && unit instanceof Builder) return unit;
      if (unitType === 'boomerang_thrower' && unit instanceof BoomerangThrower) return unit;
    }
    return null;
  }

  // Apply all purchased chain upgrades to a newly created unit
  private applyChainProgress(unit: BaseUnit, unitType: string): void {
    const chain = UPGRADE_CHAINS[unitType];
    if (!chain) return;
    const progress = this.economyManager.getChainProgress(unitType);
    for (let i = 0; i < progress; i++) {
      unit.applyChainUpgrade(chain[i].effect);
    }
  }

  /** Apply chain progress to a wall crossbow turret */
  private applyChainProgressToTurret(turret: import('../objects/buildings/WallCrossbowTurret').WallCrossbowTurret, unitType: string): void {
    const chain = UPGRADE_CHAINS[unitType];
    if (!chain) return;
    const progress = this.economyManager.getChainProgress(unitType);
    for (let i = 0; i < progress; i++) {
      turret.applyChainUpgrade(chain[i].effect);
    }
  }

  // Apply all purchased chain upgrades to a newly created building
  private applyBuildingChainProgress(building: any, unitType: string): void {
    const chain = UPGRADE_CHAINS[unitType];
    if (!chain) return;
    const progress = this.economyManager.getChainProgress(unitType);
    for (let i = 0; i < progress; i++) {
      if ('applyChainUpgrade' in building) {
        building.applyChainUpgrade(chain[i].effect);
      }
    }
  }

  // Upgrade all existing units of a type
  private handleUpgrade(unitType: string): boolean {
    // Check if this is a chain upgrade (unit-specific) or repeatable (global)
    const isChainUnit = unitType in UPGRADE_CHAINS;

    if (isChainUnit) {
      // Chain upgrade — purchase and apply to all existing instances
      const upgrade = this.economyManager.purchaseChainUpgrade(unitType);
      if (!upgrade) return false;

      const effect = upgrade.effect;

      // Handle Finger chain upgrades — apply to finger state
      if (unitType === 'finger') {
        this.applyFingerChainEffect(effect);
        return true;
      }

      // Handle Builder "Fortify" special case — wall HP bonus
      if (effect.maxWallHPBonus) {
        this.wall.addMaxHP(effect.maxWallHPBonus);
      }

      // Apply to wall-slot units
      const unitClassMap: Record<string, new (...args: any[]) => BaseUnit> = {
        rock_thrower: RockThrower,
        spear_thrower: SpearThrower,
        builder: Builder,
        club_swinger: ClubSwinger,
        archer: Archer,
        sling_thrower: SlingThrower,
        longbow_archer: LongbowArcher,
        boomerang_thrower: BoomerangThrower,
      };

      if (unitType in unitClassMap) {
        const UnitClass = unitClassMap[unitType];
        for (const unit of this.units) {
          if (unit instanceof UnitClass) {
            unit.applyChainUpgrade(effect);
          }
        }
      }

      // Apply to buildings
      const buildingSlotMap: Record<string, BuildingSlot> = {
        crow_trainer: 'below_1',
        trebuchet: 'behind_1',
        beehive_thrower: 'front_4',
        crossbow: 'front_3',
        firefly_nest: 'front_2',
        druid: 'below_2',
        lightning_wizard: 'behind_2',
      };

      if (unitType in buildingSlotMap) {
        const slotKey = buildingSlotMap[unitType];
        const building = this.buildings.get(slotKey);
        if (building && 'applyChainUpgrade' in building) {
          (building as any).applyChainUpgrade(effect);
        }
      }

      // Apply to wall crossbow turrets
      if (unitType === 'wall_crossbow_top' || unitType === 'wall_crossbow_bottom') {
        const position = unitType === 'wall_crossbow_top' ? 'top' : 'bottom';
        const turret = this.wallTurrets.find(t => t.position === position);
        if (turret) turret.applyChainUpgrade(effect);
      }

      return true;
    }

    // Repeatable global upgrades
    if (!this.economyManager.purchaseUpgrade(unitType)) return false;

    if (unitType === 'wall_upgrade') {
      this.wall.upgrade();
    }

    return true;
  }

  // Get total repair rate from all builders
  getTotalRepairRate(): number {
    let totalRate = 0;
    for (const unit of this.units) {
      if (unit instanceof Builder) {
        totalRate += unit.getRepairRate();
      }
    }
    // Convert from repair/interval to repair/second
    // Builder interval is 1000ms by default, so rate is already per second
    // But let's be precise: rate * (1000 / interval)
    // Actually Builder.ts: getRepairRate() returns amount per repair.
    // Interval is BUILDER.repairInterval (1000ms).
    // So if interval is 1s, rate is just the amount.
    return totalRate;
  }

  activateSkill(skillId: SkillId): boolean {
    // Rock Rain uses targeting mode — don't consume cooldown yet
    if (skillId === 'rock_rain') {
      if (!this.skillManager.canActivate(skillId)) return false;
      this.enterTargetingMode(skillId);
      return true;
    }

    if (!this.skillManager.activateSkill(skillId)) return false;

    gameStateManager.permanent.skillsUsed++;
    achievementManager.checkProgress('skills_used', gameStateManager.permanent.skillsUsed);

    switch (skillId) {
      case 'poison_arrows':
        this.executePoisonArrows();
        break;
      case 'gold_rush':
        this.executeGoldRush();
        break;
      case 'lightning_strike':
        this.executeLightningStrike();
        break;
    }
    return true;
  }

  private enterTargetingMode(skillId: SkillId): void {
    this.targetingSkill = skillId;
    if (skillId === 'rock_rain') {
      this.targetingRadius = this.skillManager.getSkillValue('rock_rain', 'radius');
    }
  }

  private drawTargetingCursor(x: number, y: number): void {
    if (!this.targetingGfx) return;
    this.targetingGfx.clear();
    this.targetingGfx.lineStyle(2, 0xff4444, 0.8);
    this.targetingGfx.strokeCircle(x, y, this.targetingRadius);
    this.targetingGfx.fillStyle(0xff4444, 0.15);
    this.targetingGfx.fillCircle(x, y, this.targetingRadius);
    this.targetingGfx.lineStyle(1, 0xffffff, 0.6);
    this.targetingGfx.beginPath();
    this.targetingGfx.moveTo(x - 8, y); this.targetingGfx.lineTo(x + 8, y);
    this.targetingGfx.moveTo(x, y - 8); this.targetingGfx.lineTo(x, y + 8);
    this.targetingGfx.strokePath();
  }

  private confirmTargeting(x: number, y: number): void {
    if (!this.targetingSkill) return;

    if (this.targetingSkill === 'rock_rain') {
      if (!this.skillManager.activateSkill('rock_rain')) {
        this.cancelTargeting();
        return;
      }
      this.executeRockRain(x, y);
    }

    this.targetingSkill = null;
    if (this.targetingGfx) this.targetingGfx.clear();
  }

  private cancelTargeting(): void {
    this.targetingSkill = null;
    if (this.targetingGfx) this.targetingGfx.clear();
  }

  private executePoisonArrows(): void {
    const dps = this.skillManager.getSkillValue('poison_arrows', 'damage');
    const duration = this.skillManager.getSkillValue('poison_arrows', 'duration');

    let count = 0;
    for (const enemy of this.enemies) {
      if (enemy.isDead) continue;
      enemy.applyPoison(dps, duration);
      count++;
    }

    // Visual — green arrows raining across the field
    const gfx = this.add.graphics();
    gfx.lineStyle(1.5, 0x44cc44, 0.8);
    for (let i = 0; i < 20; i++) {
      const ax = WALL_X + WALL_WIDTH + Math.random() * (GAME_WIDTH - WALL_X - WALL_WIDTH);
      const ay = ENEMY_MIN_Y + Math.random() * (ENEMY_MAX_Y - ENEMY_MIN_Y);
      // Arrow shaft
      gfx.beginPath();
      gfx.moveTo(ax, ay - 12);
      gfx.lineTo(ax, ay + 4);
      gfx.strokePath();
      // Arrowhead
      gfx.fillStyle(0x44cc44, 0.8);
      gfx.fillTriangle(ax - 3, ay + 2, ax + 3, ay + 2, ax, ay + 7);
    }
    // Green tint over field
    gfx.fillStyle(0x44cc44, 0.12);
    gfx.fillRect(WALL_X + WALL_WIDTH, 0, GAME_WIDTH - WALL_X - WALL_WIDTH, GAME_HEIGHT);

    this.tweens.add({
      targets: gfx,
      alpha: 0,
      duration: 800,
      onComplete: () => gfx.destroy(),
    });

    this.showFloatingText(GAME_WIDTH / 2, GAME_HEIGHT / 3, `Poison! ${dps} DPS (${count} hit)`, 0x44cc44);
  }

  private executeRockRain(targetX: number, targetY: number): void {
    const damage = this.skillManager.getSkillValue('rock_rain', 'damage');
    const radius = this.skillManager.getSkillValue('rock_rain', 'radius');

    for (const enemy of this.enemies) {
      if (enemy.isDead) continue;
      const dx = enemy.x - targetX;
      const dy = enemy.y - targetY;
      if (Math.sqrt(dx * dx + dy * dy) <= radius) {
        const wasKilled = enemy.takeDamage(damage);
        if (wasKilled) this.processEnemyKill(enemy);
      }
    }

    const gfx = this.add.graphics();
    gfx.fillStyle(0x777777, 0.6);
    gfx.fillCircle(targetX, targetY, radius);
    gfx.fillStyle(0xaaaaaa, 0.8);
    for (let i = 0; i < 8; i++) {
      const rx = targetX + (Math.random() - 0.5) * radius * 1.5;
      const ry = targetY + (Math.random() - 0.5) * radius * 1.5;
      gfx.fillCircle(rx, ry, 4 + Math.random() * 4);
    }
    this.tweens.add({
      targets: gfx,
      alpha: 0,
      duration: 600,
      onComplete: () => gfx.destroy(),
    });

    this.showFloatingText(targetX, targetY - 30, `Rain! -${damage}`, 0xaaaaaa);
  }

  private executeGoldRush(): void {
    const multiplier = this.skillManager.getSkillValue('gold_rush', 'multiplier');
    const duration = this.skillManager.getSkillValue('gold_rush', 'duration');

    this.goldRushActive = true;
    this.goldRushMultiplier = multiplier;
    this.goldRushTimer = duration;
    this.goldRushDuration = duration;

    this.showFloatingText(GAME_WIDTH / 2, GAME_HEIGHT / 3, `Gold Rush! ${multiplier}x gold (${(duration / 1000).toFixed(0)}s)`, 0xffcc00);

    const gfx = this.add.graphics();
    gfx.fillStyle(0xffcc00, 0.2);
    gfx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    this.tweens.add({
      targets: gfx,
      alpha: 0,
      duration: 600,
      onComplete: () => gfx.destroy(),
    });
  }

  private executeLightningStrike(): void {
    const damage = this.skillManager.getSkillValue('lightning_strike', 'damage');
    const chains = this.skillManager.getSkillValue('lightning_strike', 'chains');
    const config = SKILLS.lightning_strike;

    // Find enemy with most HP
    let target: Enemy | null = null;
    let maxHP = 0;
    for (const enemy of this.enemies) {
      if (enemy.isDead) continue;
      if (enemy.hp > maxHP) {
        maxHP = enemy.hp;
        target = enemy;
      }
    }

    if (!target) return;

    const primaryKilled = target.takeDamage(damage);
    if (primaryKilled) this.processEnemyKill(target);
    this.showFloatingText(target.x, target.y - 30, `${damage}`, 0xffff00);

    const gfx = this.add.graphics();
    gfx.lineStyle(3, 0xffff00, 1);
    gfx.beginPath();
    gfx.moveTo(target.x, 0);
    let lx = target.x;
    let ly = 0;
    const segments = 6;
    const segH = target.y / segments;
    for (let i = 0; i < segments; i++) {
      lx += (Math.random() - 0.5) * 30;
      ly += segH;
      gfx.lineTo(lx, ly);
    }
    gfx.lineTo(target.x, target.y);
    gfx.strokePath();

    let currentTarget = target;
    let chainDamage = damage;
    for (let c = 0; c < chains; c++) {
      chainDamage = Math.round(chainDamage * config.chainDamageDecay);
      let nearest: Enemy | null = null;
      let minDist = 150;

      for (const enemy of this.enemies) {
        if (enemy.isDead || enemy === currentTarget) continue;
        const dx = enemy.x - currentTarget.x;
        const dy = enemy.y - currentTarget.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < minDist) {
          minDist = dist;
          nearest = enemy;
        }
      }

      if (!nearest) break;

      const chainKilled = nearest.takeDamage(chainDamage);
      if (chainKilled) this.processEnemyKill(nearest);
      gfx.lineStyle(2, 0x88ccff, 0.8);
      gfx.beginPath();
      gfx.moveTo(currentTarget.x, currentTarget.y);
      gfx.lineTo(nearest.x, nearest.y);
      gfx.strokePath();

      currentTarget = nearest;
    }

    this.tweens.add({
      targets: gfx,
      alpha: 0,
      duration: 400,
      onComplete: () => gfx.destroy(),
    });
  }

  // ---- The Finger: Click Damage ----

  applyFingerChainEffect(effect: import('../types').ChainUpgradeEffect): void {
    if (effect.fingerDamageFlat) this.fingerDamageFlat += effect.fingerDamageFlat;
    if (effect.fingerDamageMult) this.fingerChainMult *= (1 + effect.fingerDamageMult);
    if (effect.fingerClickGold) this.fingerClickGold += effect.fingerClickGold;
    if (effect.fingerAoeRadius) this.fingerAoeRadius = effect.fingerAoeRadius; // set, not add
    if (effect.fingerAutoClick) {
      this.fingerAutoClickInterval = effect.fingerAutoClick;
      this.fingerAutoClickTimer = effect.fingerAutoClick;
    }
  }

  private handleFingerClick(px: number, py: number): void {
    // Check if click is on the battlefield (right of wall)
    if (px < WALL_X + WALL_WIDTH) return;
    this.totalClicks++;
    achievementManager.checkProgress('clicks', this.totalClicks);
    this.applyFingerDamage(px, py);
  }

  private applyFingerDamage(px: number, py: number): void {
    const baseDamage = this.economyManager.getClickDamage() + this.fingerDamageFlat;
    const chainMult = this.fingerChainMult;
    const clickAchBonus = achievementManager.getBonuses().clickDamage;
    const totalDamage = Math.round(baseDamage * chainMult * this.prestigeMult * (1 + clickAchBonus));

    gameStateManager.permanent.totalClickDamage += totalDamage;
    achievementManager.checkProgress('click_total_damage', gameStateManager.permanent.totalClickDamage);

    let hitCount = 0;

    if (this.fingerAoeRadius > 0) {
      // AoE click — damage all enemies in radius
      for (const enemy of this.enemies) {
        if (enemy.isDead || enemy.enemyType === 'boulder') continue;
        const dx = enemy.x - px;
        const dy = enemy.y - py;
        if (Math.sqrt(dx * dx + dy * dy) <= this.fingerAoeRadius) {
          const killed = enemy.takeDamage(totalDamage, false, true);
          if (killed) this.processEnemyKill(enemy);
          hitCount++;
        }
      }
    } else {
      // Single target — find nearest enemy to click point
      let nearest: Enemy | null = null;
      let minDist = 50; // Max click distance
      for (const enemy of this.enemies) {
        if (enemy.isDead || enemy.enemyType === 'boulder') continue;
        const dx = enemy.x - px;
        const dy = enemy.y - py;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < minDist) {
          minDist = dist;
          nearest = enemy;
        }
      }
      if (nearest) {
        const killed = nearest.takeDamage(totalDamage, false, true);
        if (killed) this.processEnemyKill(nearest);
        hitCount = 1;
      }
    }

    // Gold per click
    if (this.fingerClickGold > 0 && hitCount > 0) {
      const rushMult = this.goldRushActive ? this.goldRushMultiplier : 1;
      const achGoldBonus = achievementManager.getBonuses().gold;
      const clickGold = this.fingerClickGold * rushMult * this.prestigeGoldMult * (1 + achGoldBonus) * (1 + this.elderBonuses.clickGoldMult);
      this.economyManager.addGold(clickGold);
      this.totalGoldEarned += clickGold;
      this.goldEarnedThisRun += clickGold;
      this.floatingTextManager.spawn(px, py + 15, `+${formatNumber(Math.floor(clickGold))}g`, 'gold');
    }

    // Visual feedback
    if (hitCount > 0) {
      const finalDmg = Math.floor(totalDamage);
      this.floatingTextManager.spawn(px, py - 20, formatNumber(finalDmg), 'damage');

      // Impact burst effect
      this.drawClickImpact(px, py);
    }
  }

  private drawClickImpact(x: number, y: number): void {
    const gfx = this.add.graphics().setDepth(500);
    const color = 0xffffff;

    // Draw mouse pointer (cursor arrow)
    gfx.fillStyle(color, 1);
    gfx.lineStyle(1, 0x000000, 1);

    gfx.beginPath();
    // Simple cursor shape: arrow pointing top-left
    // 0,0 is tip.
    gfx.moveTo(0, 0);
    gfx.lineTo(0, 18);
    gfx.lineTo(4, 14);
    gfx.lineTo(9, 20); // Tail
    gfx.lineTo(11, 19);
    gfx.lineTo(6, 12);
    gfx.lineTo(12, 12);
    gfx.closePath();

    gfx.fillPath();
    gfx.strokePath();

    // Position at click
    gfx.setPosition(x, y);
    // Rotate slightly (-20 deg) to look natural
    gfx.setRotation(-0.35);

    // Initial scale
    gfx.setScale(0.8);

    // AoE visual (subtle ring)
    if (this.fingerAoeRadius > 0) {
      gfx.lineStyle(1, color, 0.3);
      gfx.strokeCircle(0, 0, this.fingerAoeRadius);
    }

    this.tweens.add({
      targets: gfx,
      alpha: 0,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 400,
      ease: 'Power2',
      onComplete: () => gfx.destroy(),
    });
  }

  showFloatingText(x: number, y: number, message: string, color: number = 0xffffff): void {
    const showDamage = localStorage.getItem('setting_show_damage') !== 'false';
    const showGold = localStorage.getItem('setting_show_gold') !== 'false';

    const isGold = message.toLowerCase().includes('gold') || color === 0xffcc00 || color === 0xffd700;
    if (isGold && !showGold) return;
    if (!isGold && !showDamage) return;

    const text = this.add.text(x, y, message, {
      fontFamily: 'Arial',
      fontSize: '14px',
      color: '#' + color.toString(16),
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);

    this.tweens.add({
      targets: text,
      y: y - 30,
      alpha: 0,
      duration: 1000,
      ease: 'Power1',
      onComplete: () => {
        text.destroy();
      }
    });
  }

  showImpactEffect(x: number, y: number, radius: number): void {
    const gfx = this.add.graphics();

    // Ground crater — dark circle
    gfx.fillStyle(0x3a2a1a, 0.6);
    gfx.fillCircle(x, y, radius * 0.4);

    // Dust ring — expanding outward
    gfx.lineStyle(3, 0x8b7355, 0.7);
    gfx.strokeCircle(x, y, radius * 0.5);

    // Debris particles scattered in the AOE
    gfx.fillStyle(0x888888, 0.8);
    for (let i = 0; i < 10; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = Math.random() * radius * 0.8;
      const px = x + Math.cos(angle) * dist;
      const py = y + Math.sin(angle) * dist;
      const size = 2 + Math.random() * 3;
      gfx.fillCircle(px, py, size);
    }

    // Dust cloud — light brown fill over AOE
    gfx.fillStyle(0xa08060, 0.25);
    gfx.fillCircle(x, y, radius);

    // Animate: expand the dust ring and fade everything out
    const ringGfx = this.add.graphics();
    ringGfx.lineStyle(2, 0xb09070, 0.5);
    ringGfx.strokeCircle(x, y, radius * 0.3);

    this.tweens.add({
      targets: ringGfx,
      scaleX: 2.5,
      scaleY: 2.5,
      alpha: 0,
      duration: 400,
      ease: 'Power2',
      onComplete: () => ringGfx.destroy(),
    });

    this.tweens.add({
      targets: gfx,
      alpha: 0,
      duration: 500,
      ease: 'Power1',
      onComplete: () => gfx.destroy(),
    });
  }

  // ---- Enemy Inspect Tooltip ----

  private handleEnemyClick(px: number, py: number): void {
    // Find clicked enemy
    let clicked: Enemy | null = null;
    for (const enemy of this.enemies) {
      if (enemy.isDead) continue;
      const b = enemy.getBounds();
      if (px >= b.x && px <= b.x + b.width && py >= b.y && py <= b.y + b.height) {
        clicked = enemy;
        break;
      }
    }

    if (clicked) {
      this.showEnemyTooltip(clicked);
    } else {
      this.hideEnemyTooltip();
    }
  }

  private showEnemyTooltip(enemy: Enemy): void {
    this.tooltipEnemy = enemy;

    if (!this.tooltipGfx) {
      this.tooltipGfx = this.add.graphics().setDepth(900);
    }
    if (!this.tooltipText) {
      this.tooltipText = this.add.text(0, 0, '', {
        fontSize: '11px',
        fontFamily: 'Arial, sans-serif',
        color: '#ffffff',
        lineSpacing: 2,
        padding: { left: 6, right: 6, top: 4, bottom: 4 },
      }).setDepth(901);
    }

    this.updateEnemyTooltip();
  }

  private hideEnemyTooltip(): void {
    this.tooltipEnemy = null;
    if (this.tooltipGfx) { this.tooltipGfx.clear(); }
    if (this.tooltipText) { this.tooltipText.setVisible(false); }
  }

  updateEnemyTooltip(): void {
    const enemy = this.tooltipEnemy;
    if (!enemy || enemy.isDead) {
      this.hideEnemyTooltip();
      return;
    }

    // Build tooltip text
    const typeName = enemy.enemyType.charAt(0).toUpperCase() + enemy.enemyType.slice(1);
    const title = enemy.isBoss ? `Boss ${typeName}` : typeName;
    const lines: string[] = [title];
    lines.push(`HP: ${formatNumber(Math.ceil(enemy.hp))} / ${formatNumber(Math.ceil(enemy.maxHP))}`);
    lines.push(`DMG: ${formatNumber(enemy.damage)}  SPD: ${Math.round(enemy.baseSpeed)}`);
    lines.push(`Gold: ${formatNumber(enemy.goldReward)}`);

    if (enemy.rangedDamageReduction > 0) {
      lines.push(`Shield: ${Math.round(enemy.rangedDamageReduction * 100)}% ranged block`);
    }
    if (enemy.healPercent > 0) {
      lines.push(`Heals allies: ${Math.round(enemy.healPercent * 100)}% HP/s`);
    }
    if (enemy.missChance > 0) {
      lines.push(`Miss chance: ${Math.round(enemy.missChance * 100)}%`);
    }
    if (enemy.attackRange > 0) {
      lines.push(`Range: ${enemy.attackRange}px`);
    }
    if (enemy.poisonTimer > 0) {
      lines.push(`Poisoned: ${enemy.poisonDps} DPS`);
    }
    if (enemy.stunTimer > 0) {
      lines.push(`Stunned`);
    }
    if (enemy.slowTimer > 0) {
      lines.push(`Slowed: ${Math.round((1 - enemy.slowMultiplier) * 100)}%`);
    }

    const text = lines.join('\n');
    this.tooltipText!.setText(text).setVisible(true);

    // Position above enemy
    const bounds = enemy.getBounds();
    const tw = this.tooltipText!.width;
    const th = this.tooltipText!.height;
    const padX = 8;
    const padY = 6;
    const boxW = tw + padX * 2;
    const boxH = th + padY * 2;
    let tx = enemy.x - boxW / 2;
    let ty = bounds.y - boxH - 8;

    // Clamp to screen
    if (tx < 4) tx = 4;
    if (tx + boxW > GAME_WIDTH - 4) tx = GAME_WIDTH - 4 - boxW;
    if (ty < 4) ty = bounds.y + bounds.height + 8; // flip below if no room above

    this.tooltipGfx!.clear();
    this.tooltipGfx!.fillStyle(0x1a1a2e, 0.92);
    this.tooltipGfx!.fillRoundedRect(tx, ty, boxW, boxH, 6);
    this.tooltipGfx!.lineStyle(1.5, 0x6688cc, 0.7);
    this.tooltipGfx!.strokeRoundedRect(tx, ty, boxW, boxH, 6);

    this.tooltipText!.setPosition(tx + padX, ty + padY);
  }

  // ---- Save / Load / Prestige ----

  /** Build current RunState snapshot for saving */
  private getRunState() {
    return {
      gold: this.economyManager.gold,
      unitCounts: { ...this.economyManager.unitCounts },
      freeUnitCounts: { ...this.economyManager.freeUnitCounts },
      upgradeLevels: { ...this.economyManager.upgradeLevels },
      chainProgress: { ...this.economyManager.chainProgress },
      skills: this.skillManager.getState(),
      currentWave: this.waveManager.currentWave,
      wallUpgradeLevel: this.wall.upgradeLevel,
      goldEarnedThisRun: this.goldEarnedThisRun,
      fingerChainMult: this.economyManager.fingerChainMult,
      fingerDamageFlat: this.economyManager.fingerDamageFlat,
      fingerClickGold: this.economyManager.fingerClickGold,
      fingerAoeRadius: this.economyManager.fingerAoeRadius,
      fingerAutoClickInterval: this.economyManager.fingerAutoClickInterval,
      activeLootGoblins: this.enemies
        .filter(e => e.enemyType === 'loot_goblin')
        .map(e => ({
          x: e.x,
          y: e.y,
          hp: e.hp,
          maxHP: e.maxHP,
          speed: e.baseSpeed,
          damage: e.damage,
          goldReward: e.goldReward,
          goblinStolenGold: e.goblinStolenGold
        })),
    };
  }

  /** Compute and display offline gold gain popup */
  private showOfflineGainPopup(wave: number, savedTimestamp: number | null): void {
    if (!savedTimestamp) return;
    const timeAwayMs = Date.now() - savedTimestamp;
    const minMs = OFFLINE_GAIN.minMinutes * 60 * 1000;
    if (timeAwayMs < minMs) return;

    const bonuses = this.elderBonuses;
    const maxHours = OFFLINE_GAIN.baseMaxHours + bonuses.offlineHoursBonus;
    const cappedMs = Math.min(timeAwayMs, maxHours * 3600 * 1000);
    const cappedSeconds = cappedMs / 1000;

    // Gold per second: uses same exponential formula as live kills
    const spawnInterval = Math.max(WAVE.minSpawnInterval, Math.round(WAVE.baseSpawnInterval * Math.pow(WAVE.spawnDecayPerWave, wave)));
    const enemiesPerSecond = 1000 / spawnInterval;
    const goldPerKill = SCALING.enemyBaseGold * Math.pow(1 + SCALING.enemyGoldGrowth, wave);
    const baseGPS = enemiesPerSecond * goldPerKill * OFFLINE_GAIN.efficiency;

    // Apply prestige gold multipliers (same as live kills)
    const goldMultiplier = 1 + bonuses.goldMult + bonuses.goldBonusNB;
    const goldGained = Math.max(1, Math.floor(baseGPS * cappedSeconds * goldMultiplier));

    // Compute screen position of Phaser gold icon (game coords ~842, 14 → screen pixels)
    const canvas = this.game.canvas;
    const canvasRect = canvas.getBoundingClientRect();
    const scaleX = canvasRect.width / GAME_WIDTH;
    const scaleY = canvasRect.height / GAME_HEIGHT;
    const goldTargetPos = {
      x: canvasRect.left + 842 * scaleX,
      y: canvasRect.top + 14 * scaleY,
    };

    new OfflineGainPopup(
      { timeAwayMs, cappedMs, maxHours, goldGained, wave: Math.max(1, wave), goldTargetPos },
      (gold) => {
        this.economyManager.addGold(gold);
        this.showFloatingText(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 40, `+${gold >= 1000 ? (gold / 1000).toFixed(1) + 'K' : gold}g`, 0xffd700);
      }
    );
  }

  /** Save game to localStorage */
  saveGame(): void {
    if (this.isResetting) return;

    // Sync achievement state into permanent before saving
    gameStateManager.permanent.achievements = achievementManager.getState();
    gameStateManager.updateHighestWave(this.waveManager.currentWave);
    gameStateManager.save(this.getRunState());
  }

  /** Restore game from saved RunState */
  private restoreFromSave(run: import('../managers/GameStateManager').RunState): void {
    // Restore economy
    this.economyManager.loadState({
      gold: run.gold,
      unitCounts: run.unitCounts,
      freeUnitCounts: run.freeUnitCounts || {},
      upgradeLevels: run.upgradeLevels,
      chainProgress: run.chainProgress,
      fingerChainMult: run.fingerChainMult,
      fingerDamageFlat: run.fingerDamageFlat,
      fingerClickGold: run.fingerClickGold,
      fingerAoeRadius: run.fingerAoeRadius,
      fingerAutoClickInterval: run.fingerAutoClickInterval,
    });

    // Restore gold earned this run
    this.goldEarnedThisRun = run.goldEarnedThisRun || 0;

    // Restore skills
    this.skillManager.loadState(run.skills);

    // Restore wave
    this.waveManager.setWave(run.currentWave);

    // Restore wall upgrade level
    for (let i = 0; i < run.wallUpgradeLevel; i++) {
      this.wall.upgrade();
    }
    this.wall.resetHP();

    // Reconstruct units visually based on economyManager unitCounts
    const counts = this.economyManager.unitCounts;

    // Helper to repeatedly 'buy' (place) a unit without spending gold
    const placeUnitSilently = (unitType: string, count: number) => {
      const isSkill = unitType in SKILLS;
      const isUpgrade = unitType in UPGRADE_CHAINS && !['rock_thrower', 'archer', 'club_swinger', 'sling_thrower', 'spear_thrower', 'builder', 'longbow_archer', 'crow_trainer', 'beehive_thrower', 'trebuchet', 'crossbow', 'firefly_nest', 'druid', 'lightning_wizard', 'boomerang_thrower', 'wall_crossbow_top', 'wall_crossbow_bottom'].includes(unitType);

      if (isSkill || isUpgrade) {
        // We don't place skills or upgrades physically on the board via handleBuy,
        // so we must NOT modify their economyManager.unitCounts!
        return;
      }

      // Temporarily give infinite gold to bypass economy check
      const prevGold = this.economyManager.gold;
      this.economyManager.gold = Infinity;
      // We manually adjust counts because `buyUnit` / `handleBuy` increments it inside economyManager.purchaseBuy.
      // So we must temporarily reduce the count, buy it, and restore it.
      const targetCount = count;
      this.economyManager.unitCounts[unitType] = 0; // Reset count so handleBuy increments it correctly from 0 to N

      for (let i = 0; i < targetCount; i++) {
        this.handleBuy(unitType);
      }
      // Restore actual gold
      this.economyManager.gold = prevGold;
    };

    // Define the exact intended order for wall units to ensure they are assigned 
    // to their correct wall slots from top to bottom.
    const orderedWallUnits = [
      'rock_thrower',
      'archer',
      'club_swinger',
      'sling_thrower',
      'spear_thrower',
      'boomerang_thrower'
    ];

    // Get all paid unit types from counts
    const paidUnitTypes = Object.keys(counts);

    // Sort so wall units are placed first in exact order, then others
    paidUnitTypes.sort((a, b) => {
      const idxA = orderedWallUnits.indexOf(a);
      const idxB = orderedWallUnits.indexOf(b);
      // Both are wall units -> sort by their intended order
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      // Only A is a wall unit -> A comes first
      if (idxA !== -1) return -1;
      // Only B is a wall unit -> B comes first
      if (idxB !== -1) return 1;
      // Neither are wall units, keep original order
      return 0;
    });

    // Place all paid units in the sorted order
    for (const unitType of paidUnitTypes) {
      if (counts[unitType] > 0) {
        placeUnitSilently(unitType, counts[unitType]);
      }
    }

    // Place free units from prestige bonuses (e.g. Armed Tribe start units).
    // These live in freeUnitCounts, not unitCounts, so the loop above misses them.
    const freeCounts = { ...this.economyManager.freeUnitCounts };
    const freeUnitTypes = Object.keys(freeCounts);

    // Sort free unit types using the same explicitly defined wall unit order
    freeUnitTypes.sort((a, b) => {
      const idxA = orderedWallUnits.indexOf(a);
      const idxB = orderedWallUnits.indexOf(b);
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA !== -1) return -1;
      if (idxB !== -1) return 1;
      return 0;
    });

    for (const unitType of freeUnitTypes) {
      const freeCount = freeCounts[unitType];
      if (freeCount <= 0) continue;
      const isSkill = unitType in SKILLS;
      const isUpgrade = unitType in UPGRADE_CHAINS && !['rock_thrower', 'archer', 'club_swinger', 'sling_thrower', 'spear_thrower', 'builder', 'longbow_archer', 'crow_trainer', 'beehive_thrower', 'trebuchet', 'crossbow', 'firefly_nest', 'druid', 'lightning_wizard', 'boomerang_thrower', 'wall_crossbow_top', 'wall_crossbow_bottom'].includes(unitType);
      if (isSkill || isUpgrade) continue;
      // Reset freeUnitCounts for this type so handleBuy(free=true) rebuilds it correctly
      this.economyManager.freeUnitCounts[unitType] = 0;
      for (let i = 0; i < freeCount; i++) {
        this.handleBuy(unitType, true);
      }
    }

    // Apply global upgrades (already loaded in manager, we just need to ensure wall stats match if there are wall upgrades from chain)
    // The chain progress is automatically applied to newly instantiated units inside handleBuy.

    // Restore achievements from permanent state
    achievementManager.loadState(gameStateManager.permanent.achievements);

    // Restore active loot goblins
    if (run.activeLootGoblins && run.activeLootGoblins.length > 0) {
      for (const g of run.activeLootGoblins) {
        const config: import('../types').EnemyConfig = {
          type: 'loot_goblin',
          hp: g.maxHP,
          speed: g.speed,
          damage: g.damage,
          attackInterval: 1000,
          goldReward: g.goldReward,
        };
        const goblin = new Enemy(this, g.x, g.y, config);
        goblin.hp = g.hp; // Restore current hp
        goblin.goblinStolenGold = g.goblinStolenGold; // Restore stolen gold
        this.enemies.push(goblin);
      }
    }
  }

  /** Full reset for prestige — clears run state, preserves permanent */
  resetForPrestige(): void {
    // 1. Award legacy and perform prestige
    const elderB = this.elderBonuses;

    // Track best single-run gold before resetting
    if (this.goldEarnedThisRun > gameStateManager.permanent.bestSingleRunGold) {
      gameStateManager.permanent.bestSingleRunGold = this.goldEarnedThisRun;
    }
    achievementManager.checkProgress('gold_single_run', gameStateManager.permanent.bestSingleRunGold);

    this.prestigeManager.performPrestige(this.goldEarnedThisRun);

    // Track prestige and legacy achievements
    achievementManager.checkProgress('prestige_count', gameStateManager.permanent.prestige.prestigeCount);
    achievementManager.checkProgress('legacy_earned', gameStateManager.permanent.prestige.totalLegacy);
    achievementManager.checkProgress('legacy_spent', gameStateManager.permanent.prestige.spentLegacy);

    // 2. Calculate elder bonuses to apply after reset (use fresh bonuses)
    this._elderBonusCache = null; // Invalidate cache
    const newElderB = this.elderBonuses;

    // 3. Reset all managers
    this.economyManager.reset();
    this.skillManager.reset();
    this.waveManager.reset();

    // 4. Destroy all scene objects
    for (const unit of this.units) unit.destroy();
    this.units.length = 0;
    for (const [, building] of this.buildings) (building as any).destroy?.();
    this.buildings.clear();
    for (const turret of this.wallTurrets) turret.destroy();
    this.wallTurrets.length = 0;
    if (this.autoClicker) {
      this.autoClicker.destroy();
      this.autoClicker = null;
    }
    for (const enemy of this.enemies) enemy.destroy();
    this.enemies.length = 0;
    for (const proj of this.projectiles) proj.destroy();
    this.projectiles.length = 0;
    for (const arrow of this.enemyArrows) arrow.destroy();
    this.enemyArrows.length = 0;
    for (const swarm of this.beeSwarms) swarm.destroy();
    this.beeSwarms.length = 0;

    // 5. Reset wall
    this.wall.reset();
    this.wall.resetHP();

    // 6. Reset slot states
    for (const slot of this.baseSlots) {
      slot.occupied = false;
      slot.unitType = null;
      slot.unitRef = null;
    }
    this.builderSlotOccupied = false;

    // 7. Reset transient state
    this.goldRushActive = false;
    this.goldRushMultiplier = 1;
    this.goldRushTimer = 0;
    this.goldRushDuration = 0;
    this.activeBoss = null;
    this.isShopPhase = true;
    this.isDefeated = false;
    this.fingerAutoClickTimer = 0;
    this.goldEarnedThisRun = 0;

    this.pendingStartBonuses = true;
    
    // Notify UI that a prestige just happened
    this.events.emit('prestige-reset');
  }

  /** Called when starting a brand new run (either fresh wipe or after closing prestige tree) */
  public applyStartOfRunBonuses(): void {
    // Invalidate cache to get the absolute newest bonuses (in case they just bought them)
    this._elderBonusCache = null;
    const newElderB = this.elderBonuses;

    // Apply Mason start wall level (assumes wall was already reset)
    const startWallLevel = newElderB.startWallLevel;
    for (let i = 0; i < startWallLevel; i++) {
      this.wall.upgrade();
    }
    this.wall.resetHP();

    // Apply Merchant head start gold
    const startGold = this.prestigeManager.getStartingGold();
    if (startGold > 0) {
      this.economyManager.addGold(startGold);
    }

    // Apply New Beginning: start wave
    if (newElderB.startWave > 0) {
      this.waveManager.currentWave = newElderB.startWave;
    }

    // Apply New Beginning: start units (buy them for free)
    const startUnits = newElderB.startUnits;
    for (const [unitType, count] of Object.entries(startUnits)) {
      const existingCount = this.economyManager.getUnitCount(unitType);
      const toBuy = count - existingCount; // Only buy if we don't already have them
      for (let i = 0; i < toBuy; i++) {
        this.handleBuy(unitType, true); // true = free purchase
      }
    }

    // Place prestige-unlocked wall crossbow turrets (award 1 free unit which builds it)
    if (newElderB.unlockWallCrossbowTop && this.economyManager.getUnitCount('wall_crossbow_top') < 1) {
      this.handleBuy('wall_crossbow_top', true);
    }
    if (newElderB.unlockWallCrossbowBottom && this.economyManager.getUnitCount('wall_crossbow_bottom') < 1) {
      this.handleBuy('wall_crossbow_bottom', true);
    }

    // Apply New Beginning: auto-clicker
    this.refreshAutoClicker();

    // Redraw base slots
    this.drawBaseSlots();

    // Save clean state
    this.saveGame();
  }

  /** Called by UI when Prestige Tree is closed */
  public onPrestigeTreeClosed(): void {
    if (this.pendingStartBonuses) {
      this.pendingStartBonuses = false;
      this.applyStartOfRunBonuses();
      
      // Auto-start first wave after a short delay
      this.time.delayedCall(500, () => {
        if (this.isShopPhase && !this.isDefeated) {
          this.startWave();
        }
      });
    }
  }
}
